import { create } from 'zustand';
import { Question, Player, GameState, CharacterSpeedData, SpeedModifier, GameItem, NetProjectile, GrenadeProjectile, SessionStage, GrenadeThrowEvent } from '@/types';
import { ScoringConfig, RoundScore, DEFAULT_SCORING_CONFIG } from '@/types/ScoringTypes';
import { dbService } from '@/services/database';
import { authService } from '@/services/auth';
import { arenaRealtimeService } from '@/services/realtime';
import type { CombatEvent } from '@/types/realtime';
import { CharacterSpeedManager } from '@/utils/CharacterSpeedManager';
import { ItemEffectConverter } from '@/utils/ItemEffectConverter';
import { ScoreCalculator } from '@/utils/ScoreCalculator';
import { getTurboItem } from '@/constants/bonusItems';
import {
  GRENADE_BLAST_RADIUS,
  GRENADE_FUSE_MS,
  getGrenadeThrowDistance,
  getGrenadeThrowSpeed,
  getGrenadeTravelDurationMs
} from '@/constants/grenade';
import logger from '@/utils/logger';

type GameMode = 'standard' | 'ai';

const hasPersistentGameSession = (
  sessionId: string | null | undefined,
  gameMode: GameMode
): sessionId is string => {
  if (!sessionId) {
    return false;
  }

  if (gameMode === 'ai') {
    return false;
  }

  return sessionId !== 'direct-access-session'
    && sessionId !== 'local-game'
    && !sessionId.includes('ai-game');
};

// Memory-efficient tracking with automatic cleanup
const processedNetProjectileIds = new Set<string>();
const MAX_PROCESSED_NET_IDS = 100; // Limit set size to prevent unbounded growth

// Cleanup old IDs when limit is reached
const cleanupProcessedNetIds = () => {
  if (processedNetProjectileIds.size > MAX_PROCESSED_NET_IDS) {
    const idsArray = Array.from(processedNetProjectileIds);
    // Keep only the most recent 50 IDs
    const recentIds = idsArray.slice(-50);
    processedNetProjectileIds.clear();
    recentIds.forEach(id => processedNetProjectileIds.add(id));
    logger.debug(`Cleaned up processed net IDs: ${idsArray.length} → ${recentIds.length}`);
  }
};

const processedGrenadeThrowIds = new Set<string>();
const MAX_PROCESSED_GRENADE_IDS = 200;

// Max wall-clock age of a projectile we'll accept from session snapshots or
// late RTDB combat events. Legitimate nets/grenades live at most ~3–4s from
// createdAt (flight ≤~1s, landed state ≤2s). Anything older is a cross-round
// ghost — either Firestore still has a pre-cleanup stale write, or an RTDB
// event is being replayed after a reconnect. Rejecting at the door prevents
// the animation loop from resurrecting them and re-writing them to Firestore.
const STALE_PROJECTILE_THRESHOLD_MS = 10000;

const cleanupProcessedGrenadeIds = () => {
  if (processedGrenadeThrowIds.size <= MAX_PROCESSED_GRENADE_IDS) {
    return;
  }

  const recentIds = Array.from(processedGrenadeThrowIds).slice(-MAX_PROCESSED_GRENADE_IDS);
  processedGrenadeThrowIds.clear();
  recentIds.forEach(id => processedGrenadeThrowIds.add(id));
  logger.debug(`Cleaned up processed grenade IDs: ${recentIds.length}`);
};

const normalizeTimestamp = (value: number | { seconds: number; toMillis?: () => number } | Date | undefined): number | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === 'object') {
    if (typeof value.toMillis === 'function') {
      return value.toMillis();
    }

    if ('seconds' in value && typeof value.seconds === 'number') {
      return value.seconds * 1000;
    }
  }

  return undefined;
};

const normalizeCoordinate = (coordinate: unknown): { x: number; y: number } | null => {
  if (!coordinate || typeof coordinate !== 'object') {
    return null;
  }

  const x = Number((coordinate as { x?: unknown }).x);
  const y = Number((coordinate as { y?: unknown }).y);

  if (Number.isFinite(x) && Number.isFinite(y)) {
    return { x, y };
  }

  return null;
};

const normalizeNetProjectile = (net: unknown): NetProjectile | null => {
  if (!net || typeof net !== 'object' || typeof (net as { id?: unknown }).id !== 'string') {
    return null;
  }

  const netObj = net as {
    id: string;
    thrownBy?: unknown;
    startPosition?: unknown;
    currentPosition?: unknown;
    targetPosition?: unknown;
    createdAt?: unknown;
    deactivatedAt?: unknown;
    isActive?: unknown;
  };

  const startPosition = normalizeCoordinate(netObj.startPosition);
  const currentPosition = normalizeCoordinate(netObj.currentPosition ?? netObj.startPosition);
  const targetPosition = normalizeCoordinate(netObj.targetPosition ?? netObj.currentPosition ?? netObj.startPosition);

  if (!startPosition || !currentPosition || !targetPosition) {
    return null;
  }

  const createdAt = normalizeTimestamp(netObj.createdAt as number | { seconds: number; toMillis?: () => number } | Date | undefined) ?? Date.now();
  const deactivatedAt = normalizeTimestamp(netObj.deactivatedAt as number | { seconds: number; toMillis?: () => number } | Date | undefined);

  const normalized: NetProjectile = {
    id: netObj.id,
    thrownBy: typeof netObj.thrownBy === 'string' ? netObj.thrownBy : String(netObj.thrownBy ?? ''),
    startPosition,
    currentPosition,
    targetPosition,
    createdAt,
    isActive: Boolean(netObj.isActive)
  };

  if (deactivatedAt !== undefined) {
    normalized.deactivatedAt = deactivatedAt;
  }

  return normalized;
};

const serializeNetProjectile = (net: NetProjectile) => ({
  id: net.id,
  thrownBy: net.thrownBy,
  startPosition: { x: net.startPosition.x, y: net.startPosition.y },
  currentPosition: { x: net.currentPosition.x, y: net.currentPosition.y },
  targetPosition: { x: net.targetPosition.x, y: net.targetPosition.y },
  createdAt: net.createdAt,
  isActive: net.isActive,
  ...(net.deactivatedAt !== undefined ? { deactivatedAt: net.deactivatedAt } : {})
});

interface InitializeGameOptions {
  stages?: SessionStage[];
  questionSetId?: string | null;
  questionSetName?: string | null;
  startStageIndex?: number;
  mode?: GameMode;
}

interface GameStore {
  // Game Session Data
  sessionId: string | null;
  gameState: GameState;
  currentQuestionIndex: number;
  questions: Question[];
  stages: SessionStage[];
  currentStageIndex: number;
  questionSetId: string | null;
  questionSetName: string | null;
  players: Player[];
  isGameActive: boolean;
  gameMode: GameMode;

  // Current Player Data
  currentPlayer: Player | null;
  currentPlayerId: string | null;
  selectedAnswer: number | null;
  selectedCharacter: number | null;

  // AI Player Configuration
  aiPlayerCount: number;

  // Character Speed Management
  characterSpeedData: CharacterSpeedData | null;

  // Item and Combat System
  activeNetProjectiles: NetProjectile[];
  activeGrenadeProjectiles: GrenadeProjectile[];
  grenadeChargeStartTime: number | null;
  selectedBonusItem: GameItem | null;

  // Round-Based Scoring System
  scoringConfig: ScoringConfig;
  scoreCalculator: ScoreCalculator;
  currentRoundResponses: Map<string, { selectedAnswer: number; responseTime: number; isCorrect: boolean }>;
  roundStartTime: number;

  // Actions
  initializeGame: (sessionId: string, questions: Question[], options?: InitializeGameOptions) => void;
  advanceToNextStage: () => SessionStage | null;
  setGameState: (state: GameState) => void;
  nextQuestion: () => void;
  syncQuestionIndex: (questionIndex: number) => void;
  selectAnswer: (answerIndex: number) => Promise<void>;
  clearCurrentPlayerAnswer: () => Promise<void>;
  setSelectedCharacter: (characterId: number) => void;
  addPlayer: (player: Player) => void;
  hydrateSessionPlayers: (players: Player[], currentPlayerId?: string | null) => void;
  setCurrentPlayer: (player: Player | null) => void;
  setCurrentPlayerId: (playerId: string | null) => void;
  updatePlayerScore: (playerId: string, score: number) => void;

  // AI Player Configuration Actions
  setAiPlayerCount: (count: number) => void;
  addMultipleAiPlayers: (count: number) => void;

  // Speed Management Actions
  initializeCharacterSpeed: (speedModeId: number) => void;
  applySpeedModifier: (modifier: Omit<SpeedModifier, 'id' | 'startTime'>) => void;
  removeSpeedModifier: (modifierId: string) => void;
  cleanupExpiredSpeedModifiers: () => void;
  getCurrentCharacterSpeed: () => number;
  applyGameItemEffect: (item: GameItem) => void;
  activateTurboItem: (playerId: string) => boolean;
  applyRoundBasedItemEffects: () => void;
  cleanupRoundBasedItemEffects: () => void;

  // Debug/Development Methods
  debugApplySpeedBoost: (multiplier?: number, durationSeconds?: number) => void;
  debugGetSpeedInfo: () => object;
  debugResetSpeed: () => void;

  // Round-Based Scoring Actions
  recordPlayerResponse: (playerId: string, selectedAnswer: number, responseTime: number) => void;
  finalizeRoundScores: () => void;
  getRoundScores: (roundNumber: number) => RoundScore[];
  getCumulativeScores: () => { playerId: string; name: string; totalScore: number; roundScores: number[] }[];
  updateScoringConfig: (config: Partial<ScoringConfig>) => void;
  startNewRound: () => void;

  // Game Flow Actions
  startGame: () => void;
  endGame: () => void;
  resetGame: () => void;

  // Item Management Actions
  setSelectedBonusItem: (item: GameItem | null) => void;
  addItemToPlayer: (playerId: string, item: GameItem) => boolean;
  usePlayerItem: (playerId: string, itemType: string, options?: { additionalUpdates?: Partial<Player> }) => boolean;

  // Player Position Management
  updatePlayerPosition: (playerId: string, position: { x: number; y: number }) => void;

  // Net Combat Actions
  throwNet: (playerId: string, direction: { x: number; y: number }, currentPosition?: { x: number; y: number }) => string | null;
  syncSessionNetProjectiles: (sessionNets: NetProjectile[]) => void;
  updateNetProjectiles: (deltaTime: number) => void;
  immobilizePlayer: (playerId: string, duration: number) => void;
  updatePlayerImmobilization: () => void;

  // Grenade Combat Actions
  throwGrenade: (playerId: string, chargeAmount: number, direction: { x: number; y: number }, currentPosition?: { x: number; y: number }) => string | null;
  updateGrenadeProjectiles: (deltaTime: number) => void;
  explodeGrenade: (grenadeId: string, position: { x: number; y: number }) => void;
  applyGrenadeForce: (playerId: string, forceVector: { x: number; y: number }, duration: number) => void;
  updatePlayerPushEffects: () => void;
  syncSessionGrenadeProjectiles: (sessionGrenades: GrenadeProjectile[]) => void;
  processGrenadeThrowEventsFromSession: (players: Player[]) => void;
  setGrenadeChargeStartTime: (time: number | null) => void;
  handleInboundCombatEvent: (event: CombatEvent) => void;

  // Getters
  getCurrentQuestion: () => Question | null;
  isLastQuestion: () => boolean;
  getTotalQuestions: () => number;
  getCurrentStage: () => SessionStage | null;
  getNextStage: () => SessionStage | null;
}

export const useGameStore = create<GameStore>((set, get) => {
  const resolveCurrentPlayerId = (): string => {
    const state = get();
    return state.currentPlayer?.id || state.currentPlayerId || 'current-player';
  };

  return ({
  // Initial State
  sessionId: null,
  gameState: 'lobby',
  currentQuestionIndex: 0,
  questions: [],
  stages: [],
  currentStageIndex: -1,
  questionSetId: null,
  questionSetName: null,
  players: [],
  isGameActive: false,
  gameMode: 'standard',
  currentPlayer: null,
  currentPlayerId: null,
  selectedAnswer: null,
  selectedCharacter: null,
  characterSpeedData: null,

  // Item and Combat System Initial State
  activeNetProjectiles: [],
  activeGrenadeProjectiles: [],
  grenadeChargeStartTime: null,
  selectedBonusItem: null,

  // AI Player Configuration Initial State
  aiPlayerCount: 1,

  // Round-based scoring initial state
  scoringConfig: DEFAULT_SCORING_CONFIG,
  scoreCalculator: new ScoreCalculator(DEFAULT_SCORING_CONFIG),
  currentRoundResponses: new Map(),
  roundStartTime: Date.now(),

  // Actions
  initializeGame: (sessionId: string, questions: Question[], options?: InitializeGameOptions) => {
    // Validate questions before initializing
    if (!questions || questions.length === 0) {
      logger.error('Cannot initialize game: No questions provided');
      return;
    }

    if (!sessionId || sessionId === 'direct-access-session' || sessionId === 'local-game') {
      logger.debug('Initializing game without a persistent session id');
    }

    // Preserve existing player scores if re-initializing same session
    const state = get();
    const preserveScores = state.sessionId === sessionId && state.players.length > 0;
    const existingPlayers = preserveScores ? state.players : [];

    const stages = options?.stages ?? [];
    const questionSetId = options?.questionSetId ?? null;
    const questionSetName = options?.questionSetName ?? null;
    const resolvedGameMode: GameMode = options?.mode
      ?? (preserveScores ? state.gameMode : 'standard');

    const resolvedStageIndex = (() => {
      if (!stages.length) {
        return -1;
      }

      if (typeof options?.startStageIndex === 'number' &&
          options.startStageIndex >= 0 &&
          options.startStageIndex < stages.length) {
        return options.startStageIndex;
      }

      const firstQuestionStageIndex = stages.findIndex(stage => stage.type === 'question');
      if (firstQuestionStageIndex !== -1) {
        return firstQuestionStageIndex;
      }

      return 0;
    })();

    const resolvedQuestionIndex = (() => {
      if (stages.length && resolvedStageIndex >= 0) {
        const targetStage = stages[resolvedStageIndex];
        if (targetStage?.type === 'question') {
          const index = targetStage.questionIndex ?? 0;
          if (index >= 0 && index < questions.length) {
            return index;
          }
        }
      }
      return 0;
    })();

    logger.gameFlow(
      `Initializing game - preserveScores: ${preserveScores}, existingPlayers: ${existingPlayers.length}, stages: ${stages.length}, startStageIndex: ${resolvedStageIndex}, startQuestionIndex: ${resolvedQuestionIndex}`
    );

    set(currentState => ({
      ...currentState,
      sessionId,
      questions,
      stages,
      currentStageIndex: stages.length ? resolvedStageIndex : -1,
      questionSetId,
      questionSetName,
      gameMode: resolvedGameMode,
      currentQuestionIndex: Math.min(
        Math.max(resolvedQuestionIndex, 0),
        Math.max(questions.length - 1, 0)
      ),
      gameState: 'lobby',
      isGameActive: true,
      selectedAnswer: null,
      // Preserve players if re-initializing same session, otherwise clear
      players: preserveScores ? existingPlayers : [],
      // Reset scoring state
      currentRoundResponses: new Map(),
      roundStartTime: Date.now(),
      // Preserve selectedCharacter - don't reset it
      // Preserve currentPlayer if exists in preserved players
      currentPlayer: preserveScores ? currentState.currentPlayer : null,
      currentPlayerId: preserveScores ? currentState.currentPlayerId : null,
    }));

    // Only add fresh current player if not preserving scores
    if (!preserveScores) {
      const currentPlayer: Player = {
        id: 'current-player',
        name: 'You',
        score: 0,
        isReady: false,
        position: { x: 400, y: 300 }, // Default center position
        items: [],
        roundScores: [],
        currentRoundScore: 0
      };
      get().addPlayer(currentPlayer);
      set({ currentPlayer, currentPlayerId: currentPlayer.id });
    }
  },

  advanceToNextStage: () => {
    const state = get();

    if (!state.stages.length) {
      const nextQuestionIndex = state.currentQuestionIndex + 1;

      if (nextQuestionIndex >= state.questions.length) {
        set({
          gameState: 'finished',
          isGameActive: false
        });
        return null;
      }

      const nextQuestion = state.questions[nextQuestionIndex];
      set({
        currentQuestionIndex: nextQuestionIndex,
        selectedAnswer: null,
        gameState: 'question',
        roundStartTime: Date.now()
      });

      const generatedStage: SessionStage = {
        id: `local-question-${nextQuestion.id ?? nextQuestionIndex}`,
        type: 'question',
        question: nextQuestion,
        questionIndex: nextQuestionIndex
      };

      logger.gameFlow(`Advanced to generated stage ${generatedStage.id} (question index ${nextQuestionIndex})`);

      return generatedStage;
    }

    const nextStageIndex = state.currentStageIndex + 1;

    if (nextStageIndex >= state.stages.length) {
      set({
        currentStageIndex: nextStageIndex,
        gameState: 'finished',
        isGameActive: false
      });

      logger.gameFlow('No more stages available - game finished.');
      return null;
    }

    const nextStage = state.stages[nextStageIndex];
    const updates: Partial<GameStore> = {
      currentStageIndex: nextStageIndex,
      selectedAnswer: null
    };

    if (nextStage.type === 'question') {
      const nextQuestionIndex = typeof nextStage.questionIndex === 'number'
        ? nextStage.questionIndex
        : Math.min(state.currentQuestionIndex + 1, state.questions.length - 1);

      updates.currentQuestionIndex = nextQuestionIndex;
      updates.gameState = 'question';
      updates.roundStartTime = Date.now();
    } else if (nextStage.type === 'bonus') {
      updates.gameState = 'bonus';
    }

    set(updates);

    logger.gameFlow(`Advanced to stage ${nextStage.id} (${nextStage.type})`);

    return nextStage;
  },

  setGameState: (gameState: GameState) => {
    const currentState = get().gameState;
    logger.gameFlow(`Game state transition: "${currentState}" → "${gameState}"`);

    set({ gameState });

    const wasRunning = currentState === 'running';

    if (gameState === 'running') {
      // Belt-and-suspenders: clear stale projectile state on real entry into a
      // new round. cleanupRoundBasedItemEffects already runs on exit, but sync
      // races (Firestore compat feed) or late RTDB combat events can leave a net
      // or grenade in local arrays after the previous round. Guard with
      // !wasRunning so redundant running→running syncs don't wipe in-flight
      // projectiles mid-round.
      if (!wasRunning) {
        const snapshot = get();
        if (
          snapshot.activeNetProjectiles.length > 0 ||
          snapshot.activeGrenadeProjectiles.length > 0
        ) {
          logger.gameFlow(
            `Clearing ${snapshot.activeNetProjectiles.length} stale net(s) and ${snapshot.activeGrenadeProjectiles.length} stale grenade(s) at round entry`
          );
          set({
            activeNetProjectiles: [],
            activeGrenadeProjectiles: [],
          });
        }
      }
      logger.gameFlow(`Entering 'running' state - applying round-based item effects`);
      get().applyRoundBasedItemEffects();
    } else if (wasRunning) {
      logger.gameFlow(`Exiting 'running' state - cleaning up round-based effects`);
      get().cleanupRoundBasedItemEffects();
    }
  },

  nextQuestion: () => {
    const stage = get().advanceToNextStage();

    if (!stage) {
      get().endGame();
    }
  },

  syncQuestionIndex: (questionIndex: number) => {
    const { currentQuestionIndex, questions } = get();

    if (questionIndex !== currentQuestionIndex) {
      if (questionIndex >= 0 && questionIndex < questions.length) {
        set({
          currentQuestionIndex: questionIndex,
          selectedAnswer: null,
        });
      } else {
        logger.warn(`Invalid question index: ${questionIndex}, valid range: 0-${questions.length - 1}`);
      }
    }
  },

  selectAnswer: async (answerIndex: number) => {
    const { sessionId, currentPlayer, currentPlayerId, currentQuestionIndex, gameMode } = get();

    // Update local state immediately for responsive UI
    set({ selectedAnswer: answerIndex });

    const effectivePlayerId = currentPlayer?.id || currentPlayerId || null;

    // Submit to backend for statistics tracking
    if (effectivePlayerId && hasPersistentGameSession(sessionId, gameMode)) {
      try {
        await authService.submitStudentAnswer(sessionId, effectivePlayerId, answerIndex, currentQuestionIndex);
      } catch (error) {
        logger.error('Failed to submit answer to backend:', error);
      }
    } else if (!effectivePlayerId) {
      logger.warn('Cannot submit answer: missing sessionId or currentPlayer.id');
    }
  },

  clearCurrentPlayerAnswer: async () => {
    const { sessionId, currentPlayer, currentPlayerId, currentQuestionIndex, currentRoundResponses, gameMode } = get();
    const effectivePlayerId = currentPlayer?.id || currentPlayerId || null;

    if (!effectivePlayerId) {
      set({ selectedAnswer: null });
      logger.warn('Cannot clear answer: missing sessionId or currentPlayer.id');
      return;
    }

    const updatedRoundResponses = new Map(currentRoundResponses);
    updatedRoundResponses.delete(effectivePlayerId);

    set(state => {
      const updatedPlayers = state.players.map(player =>
        player.id === effectivePlayerId
          ? { ...player, lastAnswerIndex: undefined }
          : player
      );

      let updatedCurrentPlayer = state.currentPlayer;
      if (updatedCurrentPlayer?.id === effectivePlayerId) {
        updatedCurrentPlayer = { ...updatedCurrentPlayer, lastAnswerIndex: undefined };
      }

      return {
        selectedAnswer: null,
        currentRoundResponses: updatedRoundResponses,
        players: updatedPlayers,
        currentPlayer: updatedCurrentPlayer
      };
    });

    if (hasPersistentGameSession(sessionId, gameMode)) {
      try {
        await authService.clearStudentAnswer(sessionId, effectivePlayerId, currentQuestionIndex);
      } catch (error) {
        logger.error('Failed to clear student answer from backend:', error);
      }
    }
  },

  setSelectedCharacter: (characterId: number) => {
    set({ selectedCharacter: characterId });
    // Initialize speed mode independently of cosmetic character choice (default SpeedModeID 0)
    get().initializeCharacterSpeed(0);
  },

  // Speed Management Actions
  initializeCharacterSpeed: (speedModeId: number) => {
    const speedData = CharacterSpeedManager.createCharacterSpeedData(speedModeId);
    set({ characterSpeedData: speedData });
  },

  applySpeedModifier: (modifier: Omit<SpeedModifier, 'id' | 'startTime'>) => {
    const { characterSpeedData } = get();
    if (!characterSpeedData) return;

    logger.debug('Applying speed modifier', {
      source: modifier.source,
      multiplier: modifier.multiplier,
      duration: modifier.duration,
      durationInSeconds: modifier.duration / 1000,
      isPermanent: modifier.duration === 0
    });

    const updatedSpeedData = CharacterSpeedManager.applySpeedModifier(characterSpeedData, modifier);
    set({ characterSpeedData: updatedSpeedData });

    logger.debug('Speed modifier applied', {
      modifierCount: updatedSpeedData.modifiers.length,
      modifiers: updatedSpeedData.modifiers.map(m => ({
      source: m.source,
      multiplier: m.multiplier,
      duration: m.duration,
      age: Date.now() - m.startTime
      }))
    });
  },

  removeSpeedModifier: (modifierId: string) => {
    const { characterSpeedData } = get();
    if (!characterSpeedData) return;

    const updatedSpeedData = CharacterSpeedManager.removeSpeedModifier(characterSpeedData, modifierId);
    set({ characterSpeedData: updatedSpeedData });
  },

  cleanupExpiredSpeedModifiers: () => {
    const { characterSpeedData } = get();
    if (!characterSpeedData) return;

    const updatedSpeedData = CharacterSpeedManager.cleanupExpiredModifiers(characterSpeedData);
    if (updatedSpeedData !== characterSpeedData) {
      set({ characterSpeedData: updatedSpeedData });
    }
  },

  getCurrentCharacterSpeed: () => {
    const { characterSpeedData } = get();
    if (!characterSpeedData) return 4.0; // Default fallback speed

    // Clean up expired modifiers before returning speed
    get().cleanupExpiredSpeedModifiers();
    const updatedData = get().characterSpeedData;
    return updatedData ? updatedData.currentEffectiveSpeed : 4.0;
  },

  applyGameItemEffect: (item: GameItem) => {
    const { players } = get();
    const currentPlayerId = resolveCurrentPlayerId();
    const currentPlayer = players.find(p => p.id === currentPlayerId);

    logger.debug('Applying game item effect', {
      itemName: item.name,
      itemType: item.type,
      currentPlayerId,
      playerFound: Boolean(currentPlayer)
    });

    if (!currentPlayer) {
      console.warn(`⚠️ applyGameItemEffect aborted - current player ${currentPlayerId} not found`);
      return;
    }

    // Only handle speed_boost here since addItemToPlayer handles all other items
    if (item.type === 'speed_boost') {
      try {
        const speedModifier = ItemEffectConverter.speedBoostToSpeedModifier(
          item,
          `game_item_${item.name.toLowerCase().replace(/\s+/g, '_')}`
        );

        if (ItemEffectConverter.validateSpeedModifier(speedModifier)) {
          get().applySpeedModifier(speedModifier);
        } else {
          console.warn(`⚠️ Invalid speed boost from ${item.name}:`, speedModifier);
        }
      } catch (error) {
        console.error(`❌ Failed to apply item effect from ${item.name}:`, error);
      }
    } else {
      // All other item types are handled by addItemToPlayer
      logger.debug(`Item effect for ${item.type} is handled by addItemToPlayer`);
    }
  },

  activateTurboItem: (playerId: string) => {
    const targetPlayerId = playerId === 'current-player' ? resolveCurrentPlayerId() : playerId;
    const localPlayerId = resolveCurrentPlayerId();
    const isLocalPlayer = targetPlayerId === localPlayerId;
    const isAiPlayer = targetPlayerId.startsWith('ai-player-');

    const { players } = get();
    const player = players.find(p => p.id === targetPlayerId);

    if (!player || !player.turboAvailable || player.turboUsed) {
      logger.warn(`Turbo not available for player ${targetPlayerId}`);
      return false;
    }

    // Play turbo activation sound only for local player
    // (AI turbo visual/audio is handled in GameArena's AI movement loop)
    if (isLocalPlayer) {
      import('@/utils/audioManager').then(({ audioManager }) => {
        import('@/config/soundEffects').then(({ ITEM_SOUNDS }) => {
          if (audioManager.isReady()) {
            audioManager.play(ITEM_SOUNDS.TURBO_ACTIVATE.id);
          }
        });
      }).catch(() => {
        // Silently ignore if audio modules can't be loaded
      });
    }

    // Apply turbo speed modifier ONLY for the local human player
    // AI players manage their own speed via AiPlayerState.speedBoostMultiplier in GameArena
    if (isLocalPlayer && !isAiPlayer) {
      const turboModifier = ItemEffectConverter.createTurboEffect();
      if (ItemEffectConverter.validateSpeedModifier(turboModifier)) {
        get().applySpeedModifier(turboModifier);
      }
    }

    // Mark turbo as used for any player (including AI)
    set(state => ({
      players: state.players.map(p =>
        p.id === targetPlayerId
          ? { ...p, turboUsed: true }
          : p
      )
    }));

    const { sessionId, gameMode } = get();
    if (hasPersistentGameSession(sessionId, gameMode)) {
      const currentState = get().players.find(p => p.id === targetPlayerId);

      if (arenaRealtimeService.isAvailable() && currentState) {
        arenaRealtimeService.writePlayerState(sessionId, targetPlayerId, {
          turboUsed: currentState.turboUsed ?? false,
        });
      }

      const mirrorUpdates: Parameters<typeof authService.updatePlayerItems>[2] = {
        netCharges: currentState?.netCharges ?? 0,
        grenadeCharges: currentState?.grenadeCharges ?? 0,
        turboAvailable: currentState?.turboAvailable ?? false,
        turboUsed: currentState?.turboUsed ?? false,
        shoesEffectNextRound: currentState?.shoesEffectNextRound ?? false,
      };

      if (currentState?.position) {
        mirrorUpdates.position = {
          x: currentState.position.x,
          y: currentState.position.y,
        };
      }

      authService.updatePlayerItems(sessionId, targetPlayerId, mirrorUpdates).catch(err => {
        logger.debug(`Failed to mirror turbo state for ${targetPlayerId}:`, err);
      });
    }

    const turboItem = getTurboItem();
    logger.combat(`Turbo activated for player ${targetPlayerId}! ${turboItem.effect.duration} second boost applied.`);
    return true;
  },

  applyRoundBasedItemEffects: () => {
    const state = get();
    const localPlayerId = resolveCurrentPlayerId();
    const playerFromArray = state.players.find(player => player.id === localPlayerId);
    const localPlayer = playerFromArray ?? state.currentPlayer;

    const hasShoesFlag = Boolean(localPlayer?.shoesEffectNextRound);
    const hasShoesItem = Boolean(localPlayer?.items?.some(item => item.type === 'shoes'));
    const hasActiveShoesModifier = Boolean(
      state.characterSpeedData?.modifiers?.some(modifier => modifier.source === 'shoes_item')
    );

    logger.debug('applyRoundBasedItemEffects state', {
      localPlayerId,
      totalPlayers: state.players.length,
      playerIds: state.players.map(p => p.id),
      playerFromArrayFound: !!playerFromArray,
      playerFromArrayShoesEffect: playerFromArray?.shoesEffectNextRound,
      currentPlayerExists: !!state.currentPlayer,
      currentPlayerId: state.currentPlayer?.id,
      currentPlayerShoesEffect: state.currentPlayer?.shoesEffectNextRound,
      finalLocalPlayerName: localPlayer?.name,
      finalShoesEffectNextRound: localPlayer?.shoesEffectNextRound,
      hasShoesFlag,
      hasShoesItem,
      hasActiveShoesModifier,
      willApply: hasShoesFlag || hasShoesItem
    });

    if (!hasShoesFlag && !hasShoesItem) {
      logger.debug('applyRoundBasedItemEffects skipped: no shoes flag or item');
      return;
    }

    if (hasActiveShoesModifier) {
      logger.debug('applyRoundBasedItemEffects skipped: shoes modifier already active');
      return;
    }

    if (!localPlayer) {
      logger.debug('applyRoundBasedItemEffects skipped: no local player could be resolved');
      return;
    }

    // Ensure characterSpeedData is initialized before applying the modifier
    // This handles cases where the speed data wasn't initialized earlier in the flow
    if (!state.characterSpeedData) {
      logger.debug('Initializing character speed data before applying shoes effect');
      get().initializeCharacterSpeed(0);
    }

    // Get the current question's arena duration (or use default of 12 seconds)
    const currentQuestion = state.questions[state.currentQuestionIndex];
    const arenaDurationSeconds = currentQuestion?.arenaPlayTimeSeconds ?? 12;

    // Play speed shoes equip sound for the student only
    import('@/utils/audioManager').then(({ audioManager }) => {
      import('@/config/soundEffects').then(({ ITEM_SOUNDS }) => {
        if (audioManager.isReady()) {
          audioManager.play(ITEM_SOUNDS.SPEED_SHOES_EQUIP.id);
        }
      });
    }).catch(() => {
      // Silently ignore if audio modules can't be loaded
    });

    // Apply shoes effect (SpeedMode 1) to the local player character
    const shoesModifier = ItemEffectConverter.createShoesEffect(arenaDurationSeconds);

    if (ItemEffectConverter.validateSpeedModifier(shoesModifier)) {
      get().applySpeedModifier(shoesModifier);

      // DON'T clear the shoes flag here!
      // The flag needs to persist during the round so the UI indicator shows
      // It will be cleared at the end of the round during cleanup

      // Verify the modifier was applied
      const updatedSpeedData = get().characterSpeedData;
      logger.debug('Shoes effect applied', {
        playerId: localPlayerId,
        arenaDurationSeconds,
        currentEffectiveSpeed: updatedSpeedData?.currentEffectiveSpeed,
        baseSpeed: updatedSpeedData?.baseSpeed,
        modifierCount: updatedSpeedData?.modifiers.length,
        modifiers: updatedSpeedData?.modifiers.map(m => ({ source: m.source, multiplier: m.multiplier }))
      });
    } else {
      logger.error(`Failed to validate shoes modifier for player ${localPlayerId}`);
    }
  },

  cleanupRoundBasedItemEffects: () => {
    const { sessionId, currentPlayer, gameMode } = get();
    const localPlayerId = currentPlayer?.id ?? null;
    const hadLocalGrenadeThrow = Boolean(currentPlayer?.lastGrenadeThrow);
    const shouldSyncSession = hasPersistentGameSession(sessionId, gameMode);

    // Remove shoes effects, turbo, immobilization, push effects, grenades for next round
    set(state => {
      let updatedCurrentPlayer = state.currentPlayer;

      const updatedPlayers = state.players.map(player => {
        const updates: Partial<Player> = {};

        // Clear shoes effect AFTER it's been used this round
        if (player.shoesEffectNextRound) {
          updates.shoesEffectNextRound = false;
        }

        // Clear turbo ONLY if it was used this round
        if (player.turboUsed) {
          updates.turboAvailable = false;
          updates.turboUsed = false;
        }

        // Clear immobilization state (prevents frozen players in next round)
        if (player.isImmobilized) {
          updates.isImmobilized = false;
          updates.immobilizedUntil = undefined;
        }

        // Clear push effects (prevents pushed players in next round)
        if (player.isBeingPushed) {
          updates.isBeingPushed = false;
          updates.pushVelocity = undefined;
          updates.pushEndTime = undefined;
        }

        // Clear grenade throw metadata so future rounds don't replay previous throws
        if (player.lastGrenadeThrow) {
          updates.lastGrenadeThrow = null;
        }

        if (Object.keys(updates).length > 0) {
          const updatedPlayer = { ...player, ...updates };
          if (updatedCurrentPlayer && updatedCurrentPlayer.id === updatedPlayer.id) {
            updatedCurrentPlayer = updatedPlayer;
          }
          return updatedPlayer;
        }

        return player;
      });

      return {
        players: updatedPlayers,
        currentPlayer: updatedCurrentPlayer,
        // Clear all active projectiles when round ends
        activeNetProjectiles: [],
        activeGrenadeProjectiles: []
      };
    });

    // Clear grenade throw metadata for the local player in Firebase so new clients don't replay it
    if (shouldSyncSession && localPlayerId && hadLocalGrenadeThrow) {
      authService.updatePlayerItems(sessionId, localPlayerId, { lastGrenadeThrow: null }).catch(err => {
        logger.debug('Failed to clear grenade throw metadata in Firebase:', err);
      });
    }

    // Sync projectile clearing to Firebase
    if (shouldSyncSession) {
      import('@/services/auth').then(({ authService }) => {
        authService.updateGameSession(sessionId, {
          activeNetProjectiles: [],
          activeGrenadeProjectiles: []
        }).catch(err => {
          logger.debug('Failed to clear projectiles from Firebase:', err);
        });
      });
    }

    // Remove shoes speed modifiers
    const { characterSpeedData } = get();
    if (characterSpeedData) {
      const shoesModifiers = characterSpeedData.modifiers.filter(mod => mod.source === 'shoes_item');
      shoesModifiers.forEach(modifier => {
        get().removeSpeedModifier(modifier.id);
      });
    }

    logger.debug('Cleaned up round-based effects: immobilization, nets, items');
  },

  // Debug/Development Methods
  debugApplySpeedBoost: (multiplier: number = 1.5, durationSeconds: number = 10) => {
    const testModifier = ItemEffectConverter.createTestSpeedBoost(
      multiplier,
      durationSeconds,
      'debug_speed_boost'
    );

    if (ItemEffectConverter.validateSpeedModifier(testModifier)) {
      get().applySpeedModifier(testModifier);
      logger.debug(`DEBUG: Applied ${multiplier}x speed boost for ${durationSeconds}s`);
    } else {
      logger.warn(`DEBUG: Invalid speed boost parameters:`, testModifier);
    }
  },

  debugGetSpeedInfo: () => {
    const { characterSpeedData, selectedCharacter } = get();
    const currentSpeed = get().getCurrentCharacterSpeed();

    const info = {
      selectedCharacter,
      characterSpeedData,
      currentEffectiveSpeed: currentSpeed,
      allBaseSpeeds: CharacterSpeedManager.getAllBaseSpeeds(),
      activeModifiers: characterSpeedData?.modifiers || [],
      modifierCount: characterSpeedData?.modifiers.length || 0,
    };

    logger.debug('DEBUG: Current Speed Info:', info);
    return info;
  },

  debugResetSpeed: () => {
    // Reset to default SpeedModeID 0
    get().initializeCharacterSpeed(0);
    logger.debug('DEBUG: Reset speed to SpeedModeID 0');
  },

  // Round-Based Scoring Actions
  recordPlayerResponse: (playerId: string, selectedAnswer: number, responseTime: number) => {
    const { currentRoundResponses, questions, currentQuestionIndex } = get();
    const currentQuestion = questions[currentQuestionIndex];

    if (!currentQuestion) return;

    const isCorrect = selectedAnswer === currentQuestion.correctAnswerIndex;

    // Debug logging to track answer correctness
    logger.scoring(`📝 Recording response - Player: ${playerId}, Selected: ${selectedAnswer}, Correct: ${currentQuestion.correctAnswerIndex}, isCorrect: ${isCorrect}`);

    currentRoundResponses.set(playerId, {
      selectedAnswer,
      responseTime,
      isCorrect
    });

    // CRITICAL: Also persist the answer to player's lastAnswerIndex so ScoreScreen can display it
    // This ensures the answer persists across navigation and question transitions
    set(state => {
      const updatedPlayers = state.players.map(player =>
        player.id === playerId
          ? { ...player, lastAnswerIndex: selectedAnswer }
          : player
      );

      const updatedCurrentPlayer = state.currentPlayer?.id === playerId
        ? { ...state.currentPlayer, lastAnswerIndex: selectedAnswer }
        : state.currentPlayer;

      return {
        players: updatedPlayers,
        currentPlayer: updatedCurrentPlayer
      };
    });
  },

  finalizeRoundScores: () => {
    const {
      currentRoundResponses,
      questions,
      currentQuestionIndex,
      players,
      scoreCalculator
    } = get();

    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;

    logger.scoring(`Finalizing scores for Round ${currentQuestionIndex + 1}`);
    logger.debug('Players before score update:', players.map(p => ({ id: p.id, name: p.name, score: p.score, roundScores: p.roundScores })));

    // Calculate detailed scores for each player
    const updatedPlayers = players.map(player => {
      const response = currentRoundResponses.get(player.id);

      if (!response) {
        // Player didn't answer - score 0, but total score stays the same
        const newRoundScores = [...(player.roundScores || [])];
        newRoundScores[currentQuestionIndex] = 0;

        logger.debug(`Player ${player.name} (${player.id}) didn't answer - keeping score at ${player.score || 0}`);

        return {
          ...player,
          roundScores: newRoundScores,
          currentRoundScore: 0,
          score: player.score || 0  // Keep existing total score unchanged
        };
      }

      // Calculate detailed round score
      const roundScore = scoreCalculator.calculateRoundScore(
        player.id,
        currentQuestion,
        response.selectedAnswer,
        response.responseTime,
        currentRoundResponses,
        currentQuestionIndex + 1
      );

      // Debug: Log detailed score breakdown
      logger.scoring(`📊 Score Breakdown for ${player.name}:`);
      logger.scoring(`   ✓ Answer: ${response.selectedAnswer} | Correct: ${currentQuestion.correctAnswerIndex} | isCorrect: ${response.isCorrect}`);
      logger.scoring(`   ✓ Base Points: ${roundScore.basePoints} | Speed Bonus: ${roundScore.speedBonus}`);
      logger.scoring(`   ✓ Multiplier Bonus: ${roundScore.multiplierBonus} | Penalty: ${roundScore.penalty}`);
      logger.scoring(`   ✓ Final Round Score: ${roundScore.finalScore}`);

      // Update player's round scores array
      const newRoundScores = [...(player.roundScores || [])];
      newRoundScores[currentQuestionIndex] = roundScore.finalScore;

      // Update cumulative total score (simple accumulation)
      const previousTotalScore = player.score || 0;
      const newTotalScore = previousTotalScore + roundScore.finalScore;

      logger.scoring(`Player ${player.name} (${player.id}): Round ${currentQuestionIndex + 1} score = ${roundScore.finalScore}, Previous total = ${previousTotalScore}, New total = ${newTotalScore}`);

      return {
        ...player,
        roundScores: newRoundScores,
        currentRoundScore: roundScore.finalScore,
        score: newTotalScore
      };
    });

    set({ players: updatedPlayers });

    logger.debug('Players after score update:', updatedPlayers.map(p => ({ id: p.id, name: p.name, score: p.score, roundScores: p.roundScores })));

    // Clear round responses for next round
    set({ currentRoundResponses: new Map() });

    // Save scores to database (including roundScores array)
    updatedPlayers.forEach(async (player) => {
      const { sessionId, gameMode } = get();
      if (player.roundScores && hasPersistentGameSession(sessionId, gameMode)) {
        // Update both total score AND round scores array in Firebase
        try {
          await dbService.updatePlayerScore(
            sessionId,
            player.id,
            player.score,
            player.roundScores,
            player.currentRoundScore
          );
          logger.network(`Saved scores to Firebase for ${player.name}: total=${player.score}, roundScores=${JSON.stringify(player.roundScores)}`);
        } catch (error) {
          logger.error(`Failed to save scores for ${player.name}:`, error);
        }
      }
    });
  },

  getRoundScores: (roundNumber: number) => {
    const { players, questions } = get();
    const roundScores: RoundScore[] = [];

    players.forEach(player => {
      if (player.roundScores && player.roundScores[roundNumber - 1] !== undefined) {
        // This is a simplified version - in a full implementation,
        // we'd store detailed RoundScore objects
        const question = questions[roundNumber - 1];
        if (question) {
          roundScores.push({
            roundNumber,
            questionId: question.id,
            basePoints: player.roundScores[roundNumber - 1] || 0,
            speedBonus: 0,
            streakBonus: 0,
            multiplierBonus: 0,
            penalty: 0,
            finalScore: player.roundScores[roundNumber - 1] || 0,
            responseTime: 0,
            isCorrect: player.roundScores[roundNumber - 1] > 0,
            selectedAnswer: 0,
            playerRank: 1,
            totalCorrectAnswers: 1
          });
        }
      }
    });

    return roundScores;
  },

  getCumulativeScores: () => {
    const { players, currentQuestionIndex } = get();

    return players.map(player => ({
      playerId: player.id,
      name: player.name,
      totalScore: player.score,
      roundScores: player.roundScores?.slice(0, currentQuestionIndex + 1) || []
    })).sort((a, b) => b.totalScore - a.totalScore); // Sort by total score descending
  },

  updateScoringConfig: (newConfig: Partial<ScoringConfig>) => {
    const { scoringConfig } = get();
    const updatedConfig = { ...scoringConfig, ...newConfig };
    const newCalculator = new ScoreCalculator(updatedConfig);

    set({
      scoringConfig: updatedConfig,
      scoreCalculator: newCalculator
    });
  },

  startNewRound: () => {
    set(state => {
      const clearedPlayers = state.players.map(player => ({
        ...player,
        lastAnswerIndex: undefined
      }));

      const clearedCurrentPlayer = state.currentPlayer
        ? {
            ...state.currentPlayer,
            lastAnswerIndex: undefined
          }
        : state.currentPlayer;

      return {
        currentRoundResponses: new Map(),
        roundStartTime: Date.now(),
        selectedAnswer: null,
        players: clearedPlayers,
        currentPlayer: clearedCurrentPlayer
      };
    });
  },

  addPlayer: (player: Player) => {
    set(state => {
      // Check if player with same ID already exists
      const existingPlayer = state.players.find(p => p.id === player.id);
      if (existingPlayer) {
        return state; // Return unchanged state
      }

      const shouldSelect =
        (state.currentPlayerId === null && player.id === 'current-player') ||
        state.currentPlayerId === player.id;
      return {
        players: [...state.players, player],
        currentPlayer: shouldSelect ? player : state.currentPlayer,
        currentPlayerId: shouldSelect ? player.id : state.currentPlayerId
      };
    });
  },

  hydrateSessionPlayers: (players: Player[], currentPlayerId?: string | null) => {
    set(state => {
      const sanitizedPlayers = Array.isArray(players) ? players.filter(Boolean) : [];
      if (sanitizedPlayers.length === 0) {
        return state;
      }

      const desiredPlayerId = currentPlayerId
        ?? state.currentPlayerId
        ?? state.currentPlayer?.id
        ?? null;

      logger.player(`Hydrating ${sanitizedPlayers.length} players from session. Current local players: ${state.players.length}`);

      const mergedPlayers = sanitizedPlayers.map(incomingPlayer => {
        const localPlayer = state.players.find(p => p.id === incomingPlayer.id);

        if (!localPlayer) {
          logger.player(`New player from session: ${incomingPlayer.name} (${incomingPlayer.id}) with score ${incomingPlayer.score}`);
          return incomingPlayer;
        }

        // Merge player data, preferring LOCAL scores if they're higher (since we calculate locally first, then sync)
        // This prevents overwriting fresh local calculations with stale Firebase data
        const localScore = localPlayer.score ?? 0;
        const incomingScore = incomingPlayer.score ?? 0;
        const useLocalScore = localScore >= incomingScore;

        const localRoundScoresLength = localPlayer.roundScores?.length ?? 0;
        const incomingRoundScoresLength = incomingPlayer.roundScores?.length ?? 0;
        const useLocalRoundScores = localRoundScoresLength >= incomingRoundScoresLength;

        logger.debug(`Merging ${incomingPlayer.name}: Local score=${localScore}, Incoming score=${incomingScore}, Using ${useLocalScore ? 'LOCAL' : 'INCOMING'}`);

        const incomingHasLastAnswerIndex = Object.prototype.hasOwnProperty.call(incomingPlayer, 'lastAnswerIndex');
        const incomingHasLastGrenadeThrow = Object.prototype.hasOwnProperty.call(incomingPlayer, 'lastGrenadeThrow');

        const rtdbActive = arenaRealtimeService.isAvailable();

        const mergedPlayer: Player = {
          ...incomingPlayer,
          // Session state is authoritative for combat/item state in multiplayer.
          // Fall back to local only when the incoming payload omits a field.
          // When RTDB is active, prefer LOCAL state for combat fields since RTDB
          // listeners keep them up-to-date with lower latency than Firestore snapshots.
          items: Array.isArray(incomingPlayer.items)
            ? incomingPlayer.items
            : (localPlayer.items ?? []),
          shoesEffectNextRound: rtdbActive
            ? (localPlayer.shoesEffectNextRound ?? incomingPlayer.shoesEffectNextRound ?? false)
            : (incomingPlayer.shoesEffectNextRound ?? localPlayer.shoesEffectNextRound ?? false),
          turboAvailable: rtdbActive
            ? (localPlayer.turboAvailable ?? incomingPlayer.turboAvailable ?? false)
            : (incomingPlayer.turboAvailable ?? localPlayer.turboAvailable ?? false),
          turboUsed: rtdbActive
            ? (localPlayer.turboUsed ?? incomingPlayer.turboUsed ?? false)
            : (incomingPlayer.turboUsed ?? localPlayer.turboUsed ?? false),
          netCharges: rtdbActive
            ? (localPlayer.netCharges ?? incomingPlayer.netCharges)
            : (incomingPlayer.netCharges ?? localPlayer.netCharges),
          grenadeCharges: rtdbActive
            ? (localPlayer.grenadeCharges ?? incomingPlayer.grenadeCharges)
            : (incomingPlayer.grenadeCharges ?? localPlayer.grenadeCharges),
          // Combat flags: activation can originate on either client, so `??`
          // would drop a remote `true` when our local snapshot still says
          // `false`. OR the flags and take the later expiry so a fresh hit
          // can also extend an existing one.
          isImmobilized: !!(localPlayer.isImmobilized || incomingPlayer.isImmobilized),
          immobilizedUntil: Math.max(
            localPlayer.immobilizedUntil ?? 0,
            incomingPlayer.immobilizedUntil ?? 0,
          ) || undefined,
          isBeingPushed: !!(localPlayer.isBeingPushed || incomingPlayer.isBeingPushed),
          pushVelocity: rtdbActive
            ? (localPlayer.pushVelocity ?? incomingPlayer.pushVelocity)
            : (incomingPlayer.pushVelocity ?? localPlayer.pushVelocity),
          pushEndTime: Math.max(
            localPlayer.pushEndTime ?? 0,
            incomingPlayer.pushEndTime ?? 0,
          ) || undefined,
          // Treat missing/undefined incoming value as an authoritative clear from session state.
          lastAnswerIndex: incomingHasLastAnswerIndex ? incomingPlayer.lastAnswerIndex : undefined,
          currentRoundScore: localPlayer.currentRoundScore ?? incomingPlayer.currentRoundScore ?? 0,
          // CRITICAL: Prefer local scores since they're calculated immediately after each round
          roundScores: useLocalRoundScores ? localPlayer.roundScores : (incomingPlayer.roundScores ?? localPlayer.roundScores ?? []),
          score: useLocalScore ? localPlayer.score : (incomingPlayer.score ?? localPlayer.score ?? 0),
          lastGrenadeThrow: rtdbActive
            ? (localPlayer.lastGrenadeThrow ?? null)
            : (() => {
                if (!incomingHasLastGrenadeThrow) {
                  return localPlayer.lastGrenadeThrow ?? null;
                }

                const incomingEvent = incomingPlayer.lastGrenadeThrow ?? null;
                const localEvent = localPlayer.lastGrenadeThrow ?? null;

                if (!incomingEvent || !localEvent) {
                  return incomingEvent;
                }

                const incomingTime = incomingEvent.thrownAt ?? 0;
                const localTime = localEvent.thrownAt ?? 0;
                return incomingTime >= localTime ? incomingEvent : localEvent;
              })()
        };

        return mergedPlayer;
      });

      const mergedIds = new Set(mergedPlayers.map(player => player.id));
      const preservedAiPlayers = state.players.filter(player =>
        player.id.startsWith('ai-player-') && !mergedIds.has(player.id)
      );

      const finalPlayers = preservedAiPlayers.length > 0
        ? [...mergedPlayers, ...preservedAiPlayers]
        : mergedPlayers;

      const resolvedPlayer = desiredPlayerId
        ? finalPlayers.find(player => player.id === desiredPlayerId) || null
        : null;

      const existingPlayerStillPresent = state.currentPlayer
        ? finalPlayers.some(player => player.id === state.currentPlayer!.id)
        : false;

      const fallbackPlayer = resolvedPlayer
        ?? (existingPlayerStillPresent ? state.currentPlayer : null)
        ?? (finalPlayers.length === 1 ? finalPlayers[0] : null);

      if (preservedAiPlayers.length > 0) {
        logger.player(`Preserved ${preservedAiPlayers.length} AI players during hydration`);
      }

      logger.player(`Hydration complete. Final player count: ${finalPlayers.length}`);

      return {
        players: finalPlayers,
        currentPlayer: fallbackPlayer,
        currentPlayerId: fallbackPlayer?.id
          ?? desiredPlayerId
          ?? state.currentPlayerId
          ?? null
      };
    });
  },

  setCurrentPlayer: (player: Player | null) => {
    set({
      currentPlayer: player,
      currentPlayerId: player ? player.id : null
    });
  },

  setCurrentPlayerId: (playerId: string | null) => {
    const state = get();
    const resolvedPlayer = playerId ? state.players.find(player => player.id === playerId) || null : null;

    set({
      currentPlayerId: playerId,
      currentPlayer: resolvedPlayer ?? (playerId ? state.currentPlayer : null)
    });
  },

  updatePlayerScore: async (playerId: string, score: number) => {
    set(state => ({
      players: state.players.map(player =>
        player.id === playerId ? { ...player, score } : player
      )
    }));

    // Update in database
    const { sessionId } = get();
    if (sessionId) {
      await dbService.updatePlayerScore(sessionId, playerId, score);
    }
  },

  // Game Flow Actions
  startGame: () => {
    set({
      gameState: 'question',
      currentQuestionIndex: 0,
      isGameActive: true,
    });
  },

  endGame: () => {
    set({
      gameState: 'finished',
      isGameActive: false,
    });
  },

  resetGame: () => {
    set({
      sessionId: null,
      gameState: 'lobby',
      currentQuestionIndex: 0,
      questions: [],
      stages: [],
      currentStageIndex: -1,
      questionSetId: null,
      questionSetName: null,
      players: [],
      isGameActive: false,
      gameMode: 'standard',
      currentPlayer: null,
      selectedAnswer: null,
      selectedCharacter: null,
      characterSpeedData: null,

      // Reset item and combat system
      activeNetProjectiles: [],
      activeGrenadeProjectiles: [],
      grenadeChargeStartTime: null,
      selectedBonusItem: null,

      // Reset AI configuration
      aiPlayerCount: 1,

      // Reset scoring state
      currentRoundResponses: new Map(),
      roundStartTime: Date.now(),
    });
  },

  // Getters
  getCurrentQuestion: () => {
    const { questions, currentQuestionIndex } = get();
    return questions[currentQuestionIndex] || null;
  },

  isLastQuestion: () => {
    const { currentQuestionIndex, questions } = get();
    return currentQuestionIndex >= questions.length - 1;
  },

  getTotalQuestions: () => {
    return get().questions.length;
  },

  getCurrentStage: () => {
    const { stages, currentStageIndex } = get();
    if (currentStageIndex < 0 || currentStageIndex >= stages.length) {
      return null;
    }
    return stages[currentStageIndex];
  },

  getNextStage: () => {
    const { stages, currentStageIndex } = get();
    const nextIndex = currentStageIndex + 1;
    if (nextIndex < 0 || nextIndex >= stages.length) {
      return null;
    }
    return stages[nextIndex];
  },

  // AI Player Configuration Actions
  setAiPlayerCount: (count: number) => {
    const clampedCount = Math.max(1, Math.min(10, count));
    set({ aiPlayerCount: clampedCount });
  },

  addMultipleAiPlayers: (count: number) => {
    const clampedCount = Math.max(1, Math.min(10, count));

    // Generate unique AI players
    for (let i = 1; i <= clampedCount; i++) {
      const aiPlayer: Player = {
        id: `ai-player-${i}`,
        name: `AI Player ${i}`,
        score: 0,
        isReady: true,
        position: {
          x: 80 + (i * 40), // Spread AIs horizontally to avoid overlap
          y: 350 + (i % 2) * 100 // Alternate between two vertical positions
        },
        items: [],
        roundScores: [],
        currentRoundScore: 0
      };

      get().addPlayer(aiPlayer);
    }
  },

  // Item Management Actions
  setSelectedBonusItem: (item: GameItem | null) => {
    set({ selectedBonusItem: item });
  },

  addItemToPlayer: (playerId: string, item: GameItem) => {
    const targetPlayerId = playerId === 'current-player' ? resolveCurrentPlayerId() : playerId;
    const snapshot = get();
    const playerInArray = snapshot.players.find(player => player.id === targetPlayerId);
    const playerFromCurrent = snapshot.currentPlayer?.id === targetPlayerId ? snapshot.currentPlayer : null;
    const targetPlayer = playerInArray ?? playerFromCurrent;

    if (!targetPlayer) {
      logger.warn(`Unable to add item ${item.name} - player ${targetPlayerId} not found`);
      return false;
    }

    logger.debug('Adding bonus item to player', {
      requestedPlayerId: playerId,
      targetPlayerId,
      itemName: item.name,
      itemType: item.type,
      players: get().players.map(p => `${p.name} (${p.id})`)
    });

    const resetBonusInventory = (player: Player): Player => ({
      ...player,
      items: [],
      netCharges: 0,
      grenadeCharges: 0,
      shoesEffectNextRound: false,
      turboAvailable: false,
      turboUsed: false,
    });

    const assignItemToPlayer = (player: Player): Player => {
      const clearedPlayer = resetBonusInventory(player);

      if (item.type === 'net') {
        return {
          ...clearedPlayer,
          netCharges: 1,
          items: [item]
        };
      }

      if (item.type === 'grenade') {
        return {
          ...clearedPlayer,
          grenadeCharges: 1,
          items: [item]
        };
      }

      if (item.type === 'turbo') {
        return {
          ...clearedPlayer,
          turboAvailable: true,
          turboUsed: false,
          items: [item]
        };
      }

      if (item.type === 'shoes') {
        return {
          ...clearedPlayer,
          shoesEffectNextRound: true,
          items: [item]
        };
      }

      return {
        ...clearedPlayer,
        items: [...clearedPlayer.items, item]
      };
    };

    set(state => {
      const existingPlayer = state.players.find(player => player.id === targetPlayerId)
        ?? (state.currentPlayer?.id === targetPlayerId ? state.currentPlayer : null);
      if (!existingPlayer) {
        return state;
      }

      const updatedTargetPlayer = assignItemToPlayer(existingPlayer);
      const playerExistsInArray = state.players.some(player => player.id === targetPlayerId);
      const updatedPlayers = playerExistsInArray
        ? state.players.map(player => (
            player.id === targetPlayerId
              ? updatedTargetPlayer
              : player
          ))
        : [...state.players, updatedTargetPlayer];

      const updatedCurrentPlayer = state.currentPlayer?.id === targetPlayerId
        ? updatedTargetPlayer
        : state.currentPlayer;

      return {
        players: updatedPlayers,
        currentPlayer: updatedCurrentPlayer
      };
    });

    const updatedPlayer = get().players.find(p => p.id === targetPlayerId);
    logger.debug('Item added to player', {
      id: updatedPlayer?.id,
      name: updatedPlayer?.name,
      turboAvailable: updatedPlayer?.turboAvailable,
      turboUsed: updatedPlayer?.turboUsed,
      shoesEffectNextRound: updatedPlayer?.shoesEffectNextRound,
      items: updatedPlayer?.items.map(i => i.name)
    });

    logger.debug(`Added ${item.name} to player ${targetPlayerId}`);
    return true;
  },

  usePlayerItem: (playerId: string, itemType: string, options?: { additionalUpdates?: Partial<Player> }) => {
    const targetPlayerId = playerId === 'current-player'
      ? resolveCurrentPlayerId()
      : playerId;

    const { players, sessionId, gameMode } = get();
    const player = players.find(p => p.id === targetPlayerId);
    const additionalUpdates = options?.additionalUpdates;

    if (!player) {
      logger.warn(`Player ${playerId} not found`);
      return false;
    }

    if (itemType === 'net') {
      const netCharges = player.netCharges || 0;
      if (netCharges <= 0) {
        logger.warn(`Player ${targetPlayerId} has no net charges remaining`);
        return false;
      }

      // Consume one net charge
      set(state => {
        const updatedPlayers = state.players.map(p => {
          if (p.id !== targetPlayerId) {
            return p;
          }

          const newCharges = Math.max(0, (p.netCharges || 0) - 1);
          const patch = additionalUpdates ? { ...additionalUpdates } : {};
          return {
            ...p,
            ...patch,
            netCharges: newCharges
          };
        });

        const updatedCurrentPlayer = state.currentPlayer?.id === targetPlayerId
          ? {
              ...state.currentPlayer,
              ...(additionalUpdates ? { ...additionalUpdates } : {}),
              netCharges: Math.max(0, (state.currentPlayer.netCharges || 0) - 1)
            }
          : state.currentPlayer;

        return {
          players: updatedPlayers,
          currentPlayer: updatedCurrentPlayer
        };
      });

      // Sync charge consumption to Firebase so teacher/spectator views stay accurate
      if (hasPersistentGameSession(sessionId, gameMode)) {
        const newCharges = Math.max(0, netCharges - 1);
        const currentState = get().players.find(p => p.id === targetPlayerId);

        if (arenaRealtimeService.isAvailable() && currentState) {
          arenaRealtimeService.writePlayerState(sessionId, targetPlayerId, {
            netCharges: newCharges,
          });
        }

        const mirrorUpdates: Parameters<typeof authService.updatePlayerItems>[2] = {
          netCharges: newCharges,
          grenadeCharges: currentState?.grenadeCharges ?? 0,
          turboAvailable: currentState?.turboAvailable ?? false,
          turboUsed: currentState?.turboUsed ?? false,
          shoesEffectNextRound: currentState?.shoesEffectNextRound ?? false,
        };

        if (currentState?.position) {
          mirrorUpdates.position = {
            x: currentState.position.x,
            y: currentState.position.y,
          };
        }

        authService.updatePlayerItems(sessionId, targetPlayerId, mirrorUpdates).catch(err => {
          logger.debug(`Failed to mirror net state for ${targetPlayerId}:`, err);
        });
      }

      return true;
    } else if (itemType === 'grenade') {
      const grenadeCharges = player.grenadeCharges || 0;
      if (grenadeCharges <= 0) {
        logger.warn(`Player ${targetPlayerId} has no grenade charges remaining`);
        return false;
      }

      // Consume one grenade charge
      set(state => {
        const updatedPlayers = state.players.map(p => {
          if (p.id !== targetPlayerId) {
            return p;
          }

          const newCharges = Math.max(0, (p.grenadeCharges || 0) - 1);
          const patch = additionalUpdates ? { ...additionalUpdates } : {};
          return {
            ...p,
            ...patch,
            grenadeCharges: newCharges
          };
        });

        // Also update currentPlayer if this is them
        const updatedCurrentPlayer = state.currentPlayer?.id === targetPlayerId
          ? {
              ...state.currentPlayer,
              ...(additionalUpdates ? { ...additionalUpdates } : {}),
              grenadeCharges: Math.max(0, (state.currentPlayer.grenadeCharges || 0) - 1)
            }
          : state.currentPlayer;

        return {
          players: updatedPlayers,
          currentPlayer: updatedCurrentPlayer
        };
      });

      if (hasPersistentGameSession(sessionId, gameMode)) {
        const newCharges = Math.max(0, grenadeCharges - 1);
        const currentState = get().players.find(p => p.id === targetPlayerId);

        if (arenaRealtimeService.isAvailable() && currentState) {
          arenaRealtimeService.writePlayerState(sessionId, targetPlayerId, {
            grenadeCharges: newCharges,
          });
        }

        const mirrorUpdates: Parameters<typeof authService.updatePlayerItems>[2] = {
          grenadeCharges: newCharges,
          netCharges: currentState?.netCharges ?? 0,
          turboAvailable: currentState?.turboAvailable ?? false,
          turboUsed: currentState?.turboUsed ?? false,
          shoesEffectNextRound: currentState?.shoesEffectNextRound ?? false,
          lastGrenadeThrow: currentState?.lastGrenadeThrow ?? null,
        };

        if (currentState?.position) {
          mirrorUpdates.position = {
            x: currentState.position.x,
            y: currentState.position.y,
          };
        }

        authService.updatePlayerItems(sessionId, targetPlayerId, mirrorUpdates).catch(err => {
          logger.debug(`Failed to mirror grenade state for ${targetPlayerId}:`, err);
        });
      }

      logger.combat(`Grenade used by ${targetPlayerId}. Remaining charges: ${Math.max(0, (player.grenadeCharges || 0) - 1)}`);
      return true;
    } else {
      // For other items, check if they have it
      const hasItem = player.items.some(item => item.type === itemType);
      if (!hasItem) {
        logger.warn(`Player ${playerId} does not have item of type ${itemType}`);
        return false;
      }

      return true;
    }
  },

  // Player Position Management
  updatePlayerPosition: (playerId: string, position: { x: number; y: number }) => {
    set(state => ({
      players: state.players.map(player =>
        player.id === playerId
          ? { ...player, position }
          : player
      )
    }));
  },

  syncSessionNetProjectiles: (sessionNets: NetProjectile[]) => {
    const now = Date.now();
    const sanitizedNets = Array.isArray(sessionNets)
      ? sessionNets
          .map(normalizeNetProjectile)
          .filter((net): net is NetProjectile => {
            if (net === null) return false;
            if (now - net.createdAt > STALE_PROJECTILE_THRESHOLD_MS) {
              logger.debug('Rejecting stale net from session sync:', net.id, 'age(ms)=', now - net.createdAt);
              return false;
            }
            return true;
          })
      : [];

    const currentPlayerId = get().currentPlayer?.id || get().currentPlayerId;
    const players = get().players;
    const collisionRadius = 40;
    const CHARACTER_SIZE = 80;

    set(state => {
      if (sanitizedNets.length === 0) {
        if (state.activeNetProjectiles.length === 0) {
          return state;
        }

        processedNetProjectileIds.clear();
        return {
          activeNetProjectiles: []
        };
      }

      let hasChanges = false;
      const existingById = new Map(state.activeNetProjectiles.map(net => [net.id, net] as const));

      sanitizedNets.forEach(net => {
        const isNewNet = !existingById.has(net.id);

        if (!isNewNet) {
          return;
        }

        // Check if this net has already landed (isActive: false) and wasn't thrown by us
        if (!net.isActive && net.thrownBy !== currentPlayerId) {
          // Perform collision detection immediately for this newly-synced landed net
          players.forEach(player => {
            if (player.id === net.thrownBy) {
              return;
            }

            const playerCenterX = player.position.x + CHARACTER_SIZE / 2;
            const playerCenterY = player.position.y + CHARACTER_SIZE / 2;
            // Use squared distance to avoid expensive Math.sqrt
            const distanceSquared =
              Math.pow(playerCenterX - net.targetPosition.x, 2) +
              Math.pow(playerCenterY - net.targetPosition.y, 2);
            const collisionRadiusSquared = collisionRadius * collisionRadius;

            if (distanceSquared <= collisionRadiusSquared) {
              logger.combat('COLLISION DETECTED in Firebase sync! Immobilizing player:', player.id);
              setTimeout(() => {
                get().immobilizePlayer(player.id, 2000);
              }, 0);
            }
          });
        }

        existingById.set(net.id, net);
        processedNetProjectileIds.add(net.id);
        cleanupProcessedNetIds(); // Prevent unbounded growth
        hasChanges = true;
      });

      if (!hasChanges) {
        return state;
      }

      return {
        activeNetProjectiles: Array.from(existingById.values())
      };
    });
  },

  // Net Combat Actions
  throwNet: (playerId: string, direction: { x: number; y: number }, currentPosition?: { x: number; y: number }) => {
    const { players } = get();
    const player = players.find(p => p.id === playerId);

    if (!player) {
      return null;
    }

    const playerPos = currentPosition || player.position;
    const itemConsumed = get().usePlayerItem(playerId, 'net', {
      additionalUpdates: { position: playerPos }
    });

    if (!itemConsumed) {
      return null;
    }

    // Play net throw sound
    import('@/utils/audioManager').then(({ audioManager }) => {
      import('@/config/soundEffects').then(({ ITEM_SOUNDS }) => {
        if (audioManager.isReady()) {
          audioManager.play(ITEM_SOUNDS.NET_THROW.id);
        }
      });
    }).catch(() => {
      // Silently ignore if audio modules can't be loaded
    });

    const netId = `net-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const throwDistance = 260; // Maximum throw distance (increased 30% from 200)
    const CHARACTER_SIZE = 80; // Character size constant

    // Calculate throw origin from character center, not top-left corner
    const throwOrigin = {
      x: playerPos.x + CHARACTER_SIZE / 2,
      y: playerPos.y + CHARACTER_SIZE / 2
    };

    const targetPosition = {
      x: throwOrigin.x + (direction.x * throwDistance),
      y: throwOrigin.y + (direction.y * throwDistance)
    };

    const newNet: NetProjectile = {
      id: netId,
      thrownBy: playerId,
      startPosition: throwOrigin,
      currentPosition: throwOrigin,
      targetPosition,
      createdAt: Date.now(),
      isActive: true
    };

    processedNetProjectileIds.add(netId);
    cleanupProcessedNetIds(); // Prevent unbounded growth

    set(state => ({
      activeNetProjectiles: [...state.activeNetProjectiles, newNet]
    }));

    // Publish combat event to RTDB for other clients
    const { sessionId: netSessionId, gameMode: netGameMode } = get();
    if (hasPersistentGameSession(netSessionId, netGameMode) && arenaRealtimeService.isAvailable()) {
      arenaRealtimeService.pushCombatEvent(netSessionId, {
        type: 'net_throw',
        playerId,
        ts: Date.now(),
        data: {
          id: netId,
          startPosition: throwOrigin,
          targetPosition,
          thrownBy: playerId,
        },
      });
    }

    // Mirror initial net projectile state to Firestore for fallback viewers.
    if (hasPersistentGameSession(netSessionId, netGameMode)) {
      const netsForSync = get().activeNetProjectiles.map(serializeNetProjectile);
      authService.updateProjectiles(netSessionId, {
        activeNetProjectiles: netsForSync
      }).catch(err => {
        logger.debug('Failed to mirror net projectile to Firebase:', err);
      });
    }

    return netId;
  },

  updateNetProjectiles: () => {
    const { activeNetProjectiles, players } = get();
    const netSpeed = 400; // pixels per second
    const now = Date.now();

    let hasChanges = false;
    let shouldSyncToFirebase = false;
    const CHARACTER_SIZE = 80;
    const collisionRadius = 40;
    const collisionRadiusSquared = collisionRadius * collisionRadius;

    const updatedNets = activeNetProjectiles.map(net => {
      if (!net.isActive) {
        return net;
      }

      const timeElapsed = (now - net.createdAt) / 1000;
      const totalDistance = Math.sqrt(
        Math.pow(net.targetPosition.x - net.startPosition.x, 2) +
        Math.pow(net.targetPosition.y - net.startPosition.y, 2)
      );
      const maxTravelTime = totalDistance / netSpeed;
      const reachedTarget = timeElapsed >= maxTravelTime;
      const clampedProgress = Math.min(timeElapsed / Math.max(maxTravelTime, 0.0001), 1);
      const newPosition = {
        x: net.startPosition.x + (net.targetPosition.x - net.startPosition.x) * clampedProgress,
        y: net.startPosition.y + (net.targetPosition.y - net.startPosition.y) * clampedProgress
      };

      // Continuous collision along the flight path. The frozen targetPosition
      // check at impact alone misses targets who move during the ~0.4s flight
      // (confirmed: a target moved 164px from throw-time aim). Checking the
      // live in-flight position each frame makes the net stick to the first
      // non-shooter it overlaps.
      let hitPlayerId: string | null = null;
      for (const player of players) {
        if (player.id === net.thrownBy) continue;
        const dx = (player.position.x + CHARACTER_SIZE / 2) - newPosition.x;
        const dy = (player.position.y + CHARACTER_SIZE / 2) - newPosition.y;
        if (dx * dx + dy * dy <= collisionRadiusSquared) {
          hitPlayerId = player.id;
          break;
        }
      }

      if (hitPlayerId || reachedTarget) {
        hasChanges = true;
        shouldSyncToFirebase = true;

        if (hitPlayerId) {
          logger.combat('COLLISION DETECTED! Immobilizing player:', hitPlayerId);
          get().immobilizePlayer(hitPlayerId, 2000);
        }

        console.log(
          `[NET-COLLISION] net=${net.id.slice(-6)} thrownBy=${net.thrownBy?.slice(-6)} ` +
          (hitPlayerId
            ? `HIT player=${hitPlayerId.slice(-6)} at (${newPosition.x.toFixed(0)},${newPosition.y.toFixed(0)})`
            : `MISS target=(${net.targetPosition.x.toFixed(0)},${net.targetPosition.y.toFixed(0)})`)
        );

        // Play hit or miss sound based on collision result
        import('@/utils/audioManager').then(({ audioManager }) => {
          import('@/config/soundEffects').then(({ ITEM_SOUNDS }) => {
            if (audioManager.isReady()) {
              audioManager.play(hitPlayerId ? ITEM_SOUNDS.NET_HIT.id : ITEM_SOUNDS.NET_MISS.id);
            }
          });
        }).catch(() => {
          // Silently ignore if audio modules can't be loaded
        });

        return {
          ...net,
          isActive: false,
          currentPosition: newPosition,
          deactivatedAt: now
        };
      }

      if (newPosition.x !== net.currentPosition.x || newPosition.y !== net.currentPosition.y) {
        hasChanges = true;
        return { ...net, currentPosition: newPosition };
      }

      return net;
    });

    // Remove inactive nets after 2 seconds
    const activeNets = updatedNets.filter(net => {
      if (net.isActive) return true;
      const removalTimestamp = net.deactivatedAt ?? net.createdAt;
      const timeElapsed = now - removalTimestamp;
      const shouldKeep = timeElapsed < 2000;

      if (!shouldKeep) {
        shouldSyncToFirebase = true;
        logger.debug('Removing expired net:', net.id);
      }

      return shouldKeep;
    });

    if (!hasChanges && activeNets.length !== activeNetProjectiles.length) {
      hasChanges = true;
    }

    if (hasChanges) {
      set({ activeNetProjectiles: activeNets });
    }

    if (shouldSyncToFirebase) {
      const { sessionId, gameMode } = get();
      if (hasPersistentGameSession(sessionId, gameMode)) {
        import('@/services/auth').then(({ authService }) => {
          const netsForSync = activeNets.map(serializeNetProjectile);
          authService.updateProjectiles(sessionId, {
            activeNetProjectiles: netsForSync
          }).catch(err => {
            logger.debug('Failed to sync net updates to Firebase:', err);
          });
        });
      }
    }
  },

  immobilizePlayer: (playerId: string, duration: number) => {
    const now = Date.now();
    const immobilizedUntil = now + duration;

    logger.combat(`Immobilizing player ${playerId} for ${duration}ms`);
    console.log(`[IMMOBILIZE] target=${playerId.slice(-6)} duration=${duration}ms until=${immobilizedUntil}`);

    set(state => {
      const updatedPlayers = state.players.map(player =>
        player.id === playerId
          ? {
              ...player,
              isImmobilized: true,
              immobilizedUntil
            }
          : player
      );

      // Also update currentPlayer if this is the current player
      const updatedCurrentPlayer = state.currentPlayer?.id === playerId
        ? { ...state.currentPlayer, isImmobilized: true, immobilizedUntil }
        : state.currentPlayer;

      return {
        players: updatedPlayers,
        currentPlayer: updatedCurrentPlayer
      };
    });

    // Sync to Firebase if in multiplayer session
    const { sessionId, gameMode } = get();
    if (hasPersistentGameSession(sessionId, gameMode)) {
      if (arenaRealtimeService.isAvailable()) {
        arenaRealtimeService.writePlayerState(sessionId, playerId, {
          isImmobilized: true,
          immobilizedUntil
        });
      }

      import('@/services/auth').then(({ authService }) => {
        authService.updatePlayerImmobilization(sessionId, playerId, true, immobilizedUntil).catch(err => {
          logger.debug('Failed to sync player immobilization to Firebase:', err);
        });
      });
    }
  },

  updatePlayerImmobilization: () => {
    const now = Date.now();
    let hasChanges = false;
    const freedPlayerIds: string[] = [];

    set(state => {
      const updatedPlayers = state.players.map(player => {
        if (player.isImmobilized && player.immobilizedUntil && now >= player.immobilizedUntil) {
          hasChanges = true;
          freedPlayerIds.push(player.id);
          logger.combat(`Freeing player ${player.id} from immobilization`);
          return {
            ...player,
            isImmobilized: false,
            immobilizedUntil: undefined
          };
        }
        return player;
      });

      // Also update currentPlayer if they were freed
      const updatedCurrentPlayer = state.currentPlayer && freedPlayerIds.includes(state.currentPlayer.id)
        ? { ...state.currentPlayer, isImmobilized: false, immobilizedUntil: undefined }
        : state.currentPlayer;

      return hasChanges ? { players: updatedPlayers, currentPlayer: updatedCurrentPlayer } : state;
    });

    // Sync freed players to Firebase
    if (freedPlayerIds.length > 0) {
      const { sessionId, gameMode } = get();
      if (hasPersistentGameSession(sessionId, gameMode)) {
        if (arenaRealtimeService.isAvailable()) {
          freedPlayerIds.forEach(playerId => {
            arenaRealtimeService.writePlayerState(sessionId, playerId, {
              isImmobilized: false,
              immobilizedUntil: null
            });
          });
        }

        import('@/services/auth').then(({ authService }) => {
          freedPlayerIds.forEach(playerId => {
            authService.updatePlayerImmobilization(sessionId, playerId, false).catch(err => {
              logger.debug(`Failed to sync player ${playerId} freedom to Firebase:`, err);
            });
          });
        });
      }
    }
  },

  // ============================================================================
  // GRENADE COMBAT SYSTEM
  // ============================================================================

  processGrenadeThrowEventsFromSession: (playersFromSession: Player[]) => {
    if (!Array.isArray(playersFromSession) || playersFromSession.length === 0) {
      return;
    }

    const eventsByPlayer = new Map<string, GrenadeThrowEvent>();
    const grenadesToSpawn: GrenadeProjectile[] = [];

    playersFromSession.forEach(sessionPlayer => {
      const event = sessionPlayer.lastGrenadeThrow;
      if (!event || processedGrenadeThrowIds.has(event.id)) {
        return;
      }

      logger.debug('Detected grenade event from session:', {
        playerId: sessionPlayer.id,
        grenadeId: event.id
      });

      processedGrenadeThrowIds.add(event.id);
      cleanupProcessedGrenadeIds();

      eventsByPlayer.set(sessionPlayer.id, event);

      grenadesToSpawn.push({
        id: event.id,
        thrownBy: sessionPlayer.id,
        startPosition: { ...event.startPosition },
        currentPosition: { ...event.startPosition },
        velocity: { ...event.velocity },
        chargeAmount: event.chargeAmount,
        throwDistance: event.throwDistance,
        createdAt: event.thrownAt,
        isActive: true,
        hasExploded: false,
        bouncesRemaining: event.bouncesRemaining ?? 0
      });
    });

    if (eventsByPlayer.size === 0 && grenadesToSpawn.length === 0) {
      return;
    }

    set(state => {
      let updatedProjectiles = state.activeGrenadeProjectiles;

      if (grenadesToSpawn.length > 0) {
        const existingIds = new Set(updatedProjectiles.map(grenade => grenade.id));
        const newProjectiles = grenadesToSpawn.filter(grenade => !existingIds.has(grenade.id));

        if (newProjectiles.length > 0) {
          updatedProjectiles = [...updatedProjectiles, ...newProjectiles];
        }
      }

      if (eventsByPlayer.size === 0 && updatedProjectiles === state.activeGrenadeProjectiles) {
        return state;
      }

      const updatedPlayers = state.players.map(existingPlayer => {
        const event = eventsByPlayer.get(existingPlayer.id);
        if (!event) {
          return existingPlayer;
        }

        return {
          ...existingPlayer,
          lastGrenadeThrow: event
        };
      });

      const updatedCurrentPlayer = state.currentPlayer
        ? (() => {
            const event = eventsByPlayer.get(state.currentPlayer!.id);
            return event ? { ...state.currentPlayer!, lastGrenadeThrow: event } : state.currentPlayer!;
          })()
        : state.currentPlayer;

      return {
        activeGrenadeProjectiles: updatedProjectiles,
        players: updatedPlayers,
        currentPlayer: updatedCurrentPlayer
      };
    });
  },

  handleInboundCombatEvent: (event: CombatEvent) => {
    const { currentPlayer } = get();

    // Skip own events
    if (currentPlayer && event.playerId === currentPlayer.id) return;

    // Reject replays of stale cross-round events. RTDB may re-deliver events
    // after a reconnect; without this, a round-N throw event can spawn a
    // projectile in round N+1.
    if (
      (event.type === 'grenade_throw' || event.type === 'net_throw') &&
      Date.now() - event.ts > STALE_PROJECTILE_THRESHOLD_MS
    ) {
      logger.debug('Rejecting stale combat event:', event.type, 'age(ms)=', Date.now() - event.ts);
      return;
    }

    if (event.type === 'grenade_throw') {
      const payload = event.data;

      // Deduplicate
      if (processedGrenadeThrowIds.has(payload.id)) return;
      processedGrenadeThrowIds.add(payload.id);
      cleanupProcessedGrenadeIds();

      const newGrenade: GrenadeProjectile = {
        id: payload.id,
        thrownBy: event.playerId,
        startPosition: { ...payload.startPosition },
        currentPosition: { ...payload.startPosition },
        velocity: { ...payload.velocity },
        chargeAmount: payload.chargeAmount,
        throwDistance: payload.throwDistance,
        createdAt: event.ts,
        isActive: true,
        hasExploded: false,
        bouncesRemaining: payload.bouncesRemaining,
      };

      set(state => ({
        activeGrenadeProjectiles: [...state.activeGrenadeProjectiles, newGrenade],
      }));
    } else if (event.type === 'net_throw') {
      const payload = event.data;

      // Deduplicate
      if (processedNetProjectileIds.has(payload.id)) return;
      processedNetProjectileIds.add(payload.id);

      const newNet: NetProjectile = {
        id: payload.id,
        thrownBy: payload.thrownBy,
        startPosition: { ...payload.startPosition },
        currentPosition: { ...payload.startPosition },
        targetPosition: { ...payload.targetPosition },
        createdAt: event.ts,
        isActive: true,
      };

      set(state => ({
        activeNetProjectiles: [...state.activeNetProjectiles, newNet],
      }));
    } else if (event.type === 'immobilize') {
      const payload = event.data;
      const targetId = payload.targetPlayerId;

      set(state => {
        const updatedPlayers = state.players.map(p => {
          if (p.id !== targetId) return p;
          return {
            ...p,
            isImmobilized: true,
            immobilizedUntil: Date.now() + payload.duration,
          };
        });

        const updatedCurrentPlayer =
          state.currentPlayer?.id === targetId
            ? { ...state.currentPlayer, isImmobilized: true, immobilizedUntil: Date.now() + payload.duration }
            : state.currentPlayer;

        return { players: updatedPlayers, currentPlayer: updatedCurrentPlayer };
      });
    } else if (event.type === 'push') {
      const payload = event.data;
      const targetId = payload.targetPlayerId;

      set(state => {
        const updatedPlayers = state.players.map(p => {
          if (p.id !== targetId) return p;
          return {
            ...p,
            isBeingPushed: true,
            pushVelocity: { x: payload.direction.x * payload.force, y: payload.direction.y * payload.force },
            pushEndTime: Date.now() + 500,
          };
        });

        const updatedCurrentPlayer =
          state.currentPlayer?.id === targetId
            ? {
                ...state.currentPlayer,
                isBeingPushed: true,
                pushVelocity: { x: payload.direction.x * payload.force, y: payload.direction.y * payload.force },
                pushEndTime: Date.now() + 500,
              }
            : state.currentPlayer;

        return { players: updatedPlayers, currentPlayer: updatedCurrentPlayer };
      });
    }
  },

  /**
   * Throw a grenade with physics-based trajectory
   * @param playerId - ID of player throwing grenade
   * @param chargeAmount - 0-1 value representing how long Shift was held
   * @param direction - Normalized direction vector {x, y}
   * @param currentPosition - Optional current player position override
   * @returns Grenade ID if successful, null if failed
   */
  throwGrenade: (playerId: string, chargeAmount: number, direction: { x: number; y: number }, currentPosition?: { x: number; y: number }) => {
    const { players } = get();
    const player = players.find(p => p.id === playerId);

    if (!player) {
      logger.debug('Cannot throw grenade: player not found');
      return null;
    }

    if ((player.grenadeCharges || 0) <= 0) {
      logger.debug('Cannot throw grenade: no charges available');
      return null;
    }

    // Play grenade throw sound
    import('@/utils/audioManager').then(({ audioManager }) => {
      import('@/config/soundEffects').then(({ ITEM_SOUNDS }) => {
        if (audioManager.isReady()) {
          audioManager.play(ITEM_SOUNDS.GRENADE_THROW.id);
        }
      });
    }).catch(() => {
      // Silently ignore if audio modules can't be loaded
    });

    // Generate unique grenade ID
    const grenadeId = `grenade-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const CHARACTER_SIZE = 80;

    // Charge affects distance, while the fuse remains tied to throw time.
    const throwSpeed = getGrenadeThrowSpeed(chargeAmount);
    const throwDistance = getGrenadeThrowDistance(chargeAmount);

    // Calculate initial velocity - horizontal movement only
    const initialVelocity = {
      x: direction.x * throwSpeed,
      y: 0 // Y curve calculated dynamically based on horizontal progress
    };

    // Get throw origin (center of character)
    const playerPos = currentPosition || player.position;
    const throwOrigin = {
      x: playerPos.x + CHARACTER_SIZE / 2,
      y: playerPos.y + CHARACTER_SIZE / 2
    };

    const throwTimestamp = Date.now();

    const grenadeEvent: GrenadeThrowEvent = {
      id: grenadeId,
      thrownAt: throwTimestamp,
      startPosition: throwOrigin,
      velocity: initialVelocity,
      chargeAmount,
      throwDistance,
      bouncesRemaining: 2
    };

    const itemConsumed = get().usePlayerItem(playerId, 'grenade', {
      additionalUpdates: {
        lastGrenadeThrow: grenadeEvent,
        position: playerPos
      }
    });

    if (!itemConsumed) {
      logger.debug('Grenade use aborted: could not consume charge');
      return null;
    }

    processedGrenadeThrowIds.add(grenadeId);
    cleanupProcessedGrenadeIds();

    // Create the grenade projectile
    const newGrenade: GrenadeProjectile = {
      id: grenadeId,
      thrownBy: playerId,
      startPosition: throwOrigin,
      currentPosition: throwOrigin,
      velocity: initialVelocity,
      chargeAmount,
      throwDistance,
      createdAt: throwTimestamp,
      isActive: true,
      hasExploded: false,
      bouncesRemaining: 2 // Allow 2 bounces before auto-exploding
    };

    // Add to local state
    set(state => ({
      activeGrenadeProjectiles: [...state.activeGrenadeProjectiles, newGrenade]
    }));

    // Publish combat event to RTDB for other clients
    const { sessionId: grenadeSessionId, gameMode: grenadeGameMode } = get();
    if (hasPersistentGameSession(grenadeSessionId, grenadeGameMode) && arenaRealtimeService.isAvailable()) {
      arenaRealtimeService.pushCombatEvent(grenadeSessionId, {
        type: 'grenade_throw',
        playerId,
        ts: throwTimestamp,
        data: {
          id: grenadeId,
          startPosition: throwOrigin,
          velocity: initialVelocity,
          chargeAmount,
          throwDistance,
          bouncesRemaining: 2,
        },
      });
    }

    // Mirror initial grenade projectile state to Firestore for fallback viewers.
    if (hasPersistentGameSession(grenadeSessionId, grenadeGameMode)) {
      const grenadesForSync = get().activeGrenadeProjectiles.map(grenade => ({
        ...grenade,
        isActive: Boolean(grenade.isActive),
        hasExploded: Boolean(grenade.hasExploded),
        createdAt: Number(grenade.createdAt) || Date.now(),
        deactivatedAt: grenade.deactivatedAt ? Number(grenade.deactivatedAt) : undefined,
        chargeAmount: Number(grenade.chargeAmount) || 0,
        throwDistance: Number(grenade.throwDistance) || 240,
        bouncesRemaining: Number(grenade.bouncesRemaining) || 0,
        startPosition: { x: Number(grenade.startPosition?.x || 0), y: Number(grenade.startPosition?.y || 0) },
        currentPosition: { x: Number(grenade.currentPosition?.x || 0), y: Number(grenade.currentPosition?.y || 0) },
        velocity: { x: Number(grenade.velocity?.x || 0), y: Number(grenade.velocity?.y || 0) },
        explosionPosition: grenade.explosionPosition
          ? { x: Number(grenade.explosionPosition.x || 0), y: Number(grenade.explosionPosition.y || 0) }
          : undefined
      }));

      authService.updateProjectiles(grenadeSessionId, {
        activeGrenadeProjectiles: grenadesForSync
      }).catch(err => {
        logger.debug('Failed to mirror grenade projectile to Firebase:', err);
      });
    }

    logger.combat('Grenade thrown:', {
      grenadeId,
      chargeAmount,
      throwSpeed,
      initialVelocity,
      thrownBy: player.name
    });

    return grenadeId;
  },

  /**
   * Update all grenade projectiles physics and check for explosions
   * Called every frame from GameArena
   */
  updateGrenadeProjectiles: (_deltaTime: number) => {
    const { activeGrenadeProjectiles, players } = get();

    if (activeGrenadeProjectiles.length === 0) {
      return;
    }

    const WORLD_WIDTH = 1200;
    const now = Date.now();

    let hasChanges = false;
    const CHARACTER_SIZE = 80;
    const BLAST_RADIUS = GRENADE_BLAST_RADIUS;

    const updatedGrenades = activeGrenadeProjectiles.map(grenade => {
      if (!grenade.isActive) {
        // Grenade has exploded, check if we should remove it
        if (grenade.hasExploded && grenade.deactivatedAt && (now - grenade.deactivatedAt > 2000)) {
          // Remove after 2 seconds
          hasChanges = true;
          return null; // Will be filtered out
        }
        return grenade; // Keep showing explosion animation
      }

      const elapsedSinceThrowMs = Math.max(0, now - grenade.createdAt);
      const travelDurationMs = getGrenadeTravelDurationMs(grenade.throwDistance, grenade.velocity.x);
      const travelElapsedMs = travelDurationMs > 0
        ? Math.min(elapsedSinceThrowMs, travelDurationMs)
        : elapsedSinceThrowMs;
      const travelProgress = travelDurationMs > 0
        ? Math.min(travelElapsedMs / travelDurationMs, 1)
        : 1;

      const rawX = grenade.startPosition.x + (grenade.velocity.x * (travelElapsedMs / 1000));
      let newX = rawX;
      let hitWall = false;

      if (rawX <= 20) {
        newX = 20;
        hitWall = true;
      } else if (rawX >= WORLD_WIDTH - 20) {
        newX = WORLD_WIDTH - 20;
        hitWall = true;
      }

      const progress = hitWall ? 1 : travelProgress;

      // Parabolic curve: y = -4h * progress * (progress - 1)
      // This creates an arc that starts at 0, peaks at 0.5, returns to 0 at 1.0
      const arcHeight = 100; // Maximum height of the arc in pixels (upward)
      const curveOffset = -4 * arcHeight * progress * (progress - 1);

      const newPosition = {
        x: newX,
        y: grenade.startPosition.y - curveOffset // Subtract to go UP (negative Y), then DOWN back to start
      };

      const fuseComplete = elapsedSinceThrowMs >= GRENADE_FUSE_MS;
      const hasReachedLandingPoint = hitWall || progress >= 1;

      let updatedGrenade = {
        ...grenade,
        currentPosition: newPosition,
        velocity: {
          x: grenade.velocity.x,
          y: 0
        }
      };
      let shouldExplode = false;

      if (hasReachedLandingPoint && fuseComplete) {
        shouldExplode = true;
        logger.combat('Grenade hit ground!');
      }

      if (hitWall && fuseComplete) {
        logger.combat('Grenade hit wall!');
      }

      // Check if grenade should explode
      if (shouldExplode) {
        hasChanges = true;

        // Trigger explosion
        const explosionPos = updatedGrenade.currentPosition;
        logger.combat('GRENADE EXPLODING at', explosionPos);

        // Play grenade explosion sound
        import('@/utils/audioManager').then(({ audioManager }) => {
          import('@/config/soundEffects').then(({ ITEM_SOUNDS }) => {
            if (audioManager.isReady()) {
              audioManager.play(ITEM_SOUNDS.GRENADE_EXPLODE.id);
            }
          });
        }).catch(() => {
          // Silently ignore if audio modules can't be loaded
        });

        // Check for player collisions within blast radius
        const blastEffects: Array<{ playerId: string; forceVector: { x: number; y: number }; duration: number }> = [];

        players.forEach(player => {
          if (player.id === grenade.thrownBy) {
            return; // Don't affect the thrower
          }

          const playerCenterX = player.position.x + CHARACTER_SIZE / 2;
          const playerCenterY = player.position.y + CHARACTER_SIZE / 2;
          const dx = playerCenterX - explosionPos.x;
          const dy = playerCenterY - explosionPos.y;
          const distanceSquared = dx * dx + dy * dy;
          const BLAST_RADIUS_SQUARED = BLAST_RADIUS * BLAST_RADIUS;

          if (distanceSquared <= BLAST_RADIUS_SQUARED) {
            const distance = Math.sqrt(distanceSquared);
            logger.combat('Player', player.name, 'caught in blast! Distance:', distance);

            const angle = Math.atan2(dy, dx);
            const forceFalloff = 1 - (distance / BLAST_RADIUS);
            const pushMagnitude = 600 * (0.5 + (forceFalloff * 0.5));

            const forceVector = {
              x: Math.cos(angle) * pushMagnitude,
              y: Math.sin(angle) * pushMagnitude
            };

            logger.combat('Applying force:', forceVector, 'to player:', player.id);
            blastEffects.push({ playerId: player.id, forceVector, duration: 1500 });
          }
        });

        // Apply all blast effects locally (immediate responsiveness)
        for (const effect of blastEffects) {
          get().applyGrenadeForce(effect.playerId, effect.forceVector, effect.duration);
        }

        // Batch-sync all blast effects to Firebase in one transaction
        if (blastEffects.length > 0) {
          const { sessionId, gameMode } = get();
          if (hasPersistentGameSession(sessionId, gameMode)) {
            const pushEndTime = now + 1500;
            import('@/services/auth').then(({ authService }) => {
              authService.batchUpdatePlayerPushEffects(sessionId, blastEffects.map(e => ({
                playerId: e.playerId,
                isBeingPushed: true,
                pushVelocity: e.forceVector,
                pushEndTime
              }))).catch((err: unknown) => {
                logger.debug('Failed to batch sync blast effects to Firebase:', err);
              });
            });
          }
        }

        // Mark as exploded
        return {
          ...updatedGrenade,
          isActive: false,
          hasExploded: true,
          explosionPosition: explosionPos,
          deactivatedAt: now,
          velocity: { x: 0, y: 0 } // Stop moving
        };
      }

      hasChanges = true;
      return updatedGrenade;
    }).filter(g => g !== null) as GrenadeProjectile[];

    if (hasChanges) {
      set({ activeGrenadeProjectiles: updatedGrenades });

      // DON'T sync grenade updates to Firebase in multiplayer
      // Grenades are synced FROM Firebase only (read-only)
      // This prevents permission errors from multiple clients trying to update
    }
  },

  /**
   * Trigger grenade explosion (currently handled in updateGrenadeProjectiles)
   */
  explodeGrenade: (grenadeId: string, position: { x: number; y: number }) => {
    logger.combat('explodeGrenade called for', grenadeId, 'at', position);
    // This method is a placeholder for explicit explosion triggering
    // Currently explosions are handled automatically in updateGrenadeProjectiles
  },

  /**
   * Apply push force to a player from grenade explosion
   * @param playerId - Player to push
   * @param forceVector - Force direction and magnitude {x, y}
   * @param duration - How long push effect lasts (milliseconds)
   */
  applyGrenadeForce: (playerId: string, forceVector: { x: number; y: number }, duration: number) => {
    const now = Date.now();
    const pushEndTime = now + duration;

    logger.combat('Applying grenade force to player:', {
      playerId,
      forceVector,
      duration,
      pushEndTime
    });

    // Update local state only — Firebase sync is handled by the caller in batch
    set(state => {
      const updatedPlayers = state.players.map(player =>
        player.id === playerId
          ? {
              ...player,
              isBeingPushed: true,
              pushVelocity: forceVector,
              pushEndTime
            }
          : player
      );

      const updatedCurrentPlayer = state.currentPlayer?.id === playerId
        ? { ...state.currentPlayer, isBeingPushed: true, pushVelocity: forceVector, pushEndTime }
        : state.currentPlayer;

      return {
        players: updatedPlayers,
        currentPlayer: updatedCurrentPlayer
      };
    });
  },

  /**
   * Update all players' push effects, clearing expired ones
   * Called every frame from GameArena
   */
  updatePlayerPushEffects: () => {
    const now = Date.now();
    let hasChanges = false;
    const freedPlayerIds: string[] = [];

    set(state => {
      const updatedPlayers = state.players.map(player => {
        if (player.isBeingPushed && player.pushEndTime && now >= player.pushEndTime) {
          hasChanges = true;
          freedPlayerIds.push(player.id);
          logger.combat('Clearing push effect for player:', player.id);
          return {
            ...player,
            isBeingPushed: false,
            pushVelocity: undefined,
            pushEndTime: undefined
          };
        }
        return player;
      });

      // Also update currentPlayer if they were freed
      const updatedCurrentPlayer = state.currentPlayer && freedPlayerIds.includes(state.currentPlayer.id)
        ? { ...state.currentPlayer, isBeingPushed: false, pushVelocity: undefined, pushEndTime: undefined }
        : state.currentPlayer;

      return hasChanges ? { players: updatedPlayers, currentPlayer: updatedCurrentPlayer } : state;
    });

    // Batch-sync freed players to Firebase in one transaction
    if (freedPlayerIds.length > 0) {
      const { sessionId, gameMode } = get();
      if (hasPersistentGameSession(sessionId, gameMode)) {
        import('@/services/auth').then(({ authService }) => {
          authService.batchUpdatePlayerPushEffects(
            sessionId,
            freedPlayerIds.map(playerId => ({ playerId, isBeingPushed: false }))
          ).catch((err: unknown) => {
            logger.debug('Failed to batch sync push removal:', err);
          });
        });
      }
    }
  },

  /**
   * Sync grenade projectiles from Firebase session
   * @param sessionGrenades - Grenade projectiles from Firebase
   */
  syncSessionGrenadeProjectiles: (sessionGrenades: GrenadeProjectile[]) => {
    logger.debug('syncSessionGrenadeProjectiles called with', sessionGrenades?.length || 0, 'grenades');

    const now = Date.now();
    const sanitizedGrenades = Array.isArray(sessionGrenades)
      ? sessionGrenades
          .map(grenade => ({
            ...grenade,
            isActive: Boolean(grenade.isActive),
            hasExploded: Boolean(grenade.hasExploded),
            createdAt: Number(grenade.createdAt) || Date.now(),
            deactivatedAt: grenade.deactivatedAt ? Number(grenade.deactivatedAt) : undefined,
            chargeAmount: Number(grenade.chargeAmount) || 0,
            throwDistance: Number(grenade.throwDistance) || 240,
            bouncesRemaining: Number(grenade.bouncesRemaining) || 0,
            startPosition: { x: Number(grenade.startPosition?.x || 0), y: Number(grenade.startPosition?.y || 0) },
            currentPosition: { x: Number(grenade.currentPosition?.x || 0), y: Number(grenade.currentPosition?.y || 0) },
            velocity: { x: Number(grenade.velocity?.x || 0), y: Number(grenade.velocity?.y || 0) },
            explosionPosition: grenade.explosionPosition
              ? { x: Number(grenade.explosionPosition.x || 0), y: Number(grenade.explosionPosition.y || 0) }
              : undefined
          }))
          .filter(grenade => {
            if (now - grenade.createdAt > STALE_PROJECTILE_THRESHOLD_MS) {
              logger.debug('Rejecting stale grenade from session sync:', grenade.id, 'age(ms)=', now - grenade.createdAt);
              return false;
            }
            return true;
          })
      : [];

    const currentPlayerId = get().currentPlayer?.id || get().currentPlayerId;
    const players = get().players;
    const BLAST_RADIUS = GRENADE_BLAST_RADIUS;
    const CHARACTER_SIZE = 80;

    set(state => {
      if (sanitizedGrenades.length === 0) {
        if (state.activeGrenadeProjectiles.length === 0) {
          return state;
        }

        const hasLiveLocalGrenade = state.activeGrenadeProjectiles.some(grenade => {
          const ageMs = now - grenade.createdAt;
          if (ageMs > STALE_PROJECTILE_THRESHOLD_MS) {
            return false;
          }

          if (grenade.isActive) {
            return true;
          }

          return grenade.hasExploded && (
            !grenade.deactivatedAt ||
            now - grenade.deactivatedAt <= 2000
          );
        });

        if (hasLiveLocalGrenade) {
          return state;
        }

        return {
          activeGrenadeProjectiles: []
        };
      }

      let hasChanges = false;
      const existingById = new Map(state.activeGrenadeProjectiles.map(g => [g.id, g]));

      sanitizedGrenades.forEach(grenade => {
        const existingGrenade = existingById.get(grenade.id);
        const isNewGrenade = !existingGrenade;

        if (!isNewGrenade && existingGrenade) {
          // Preserve local in-flight simulation and only adopt terminal snapshots.
          const shouldAdoptIncoming =
            grenade.hasExploded ||
            !grenade.isActive ||
            !existingGrenade.isActive;

          if (shouldAdoptIncoming) {
            existingById.set(grenade.id, grenade);
            hasChanges = true;
          }
          return;
        }

        if (processedGrenadeThrowIds.has(grenade.id)) {
          return;
        }

        const now = Date.now();
        const normalizedNewGrenade = (grenade.isActive && !grenade.hasExploded)
          ? {
              ...grenade,
              createdAt: Math.min(grenade.createdAt, now)
            }
          : grenade;

        logger.debug('NEW grenade from Firebase:', {
          grenadeId: normalizedNewGrenade.id,
          isActive: normalizedNewGrenade.isActive,
          hasExploded: normalizedNewGrenade.hasExploded,
          thrownBy: normalizedNewGrenade.thrownBy
        });

        // Check if this grenade has already exploded and wasn't thrown by us
        if (normalizedNewGrenade.hasExploded && normalizedNewGrenade.thrownBy !== currentPlayerId && normalizedNewGrenade.explosionPosition) {
          logger.combat('NEW exploded grenade detected! Checking collision');

          // Perform collision detection immediately for explosion
          players.forEach(player => {
            if (player.id === normalizedNewGrenade.thrownBy) return;

            const playerCenterX = player.position.x + CHARACTER_SIZE / 2;
            const playerCenterY = player.position.y + CHARACTER_SIZE / 2;
            const dx = playerCenterX - normalizedNewGrenade.explosionPosition!.x;
            const dy = playerCenterY - normalizedNewGrenade.explosionPosition!.y;
            const distanceSquared = dx * dx + dy * dy;
            const BLAST_RADIUS_SQUARED = BLAST_RADIUS * BLAST_RADIUS;

            if (distanceSquared <= BLAST_RADIUS_SQUARED) {
              logger.combat('COLLISION DETECTED in Firebase sync!');

              // Only calculate actual distance when needed
              const distance = Math.sqrt(distanceSquared);
              const angle = Math.atan2(dy, dx);
              const forceFalloff = 1 - (distance / BLAST_RADIUS);
              const pushMagnitude = 600 * (0.5 + (forceFalloff * 0.5)); // 300-600 pixels (20% reduction from 750)

              const forceVector = {
                x: Math.cos(angle) * pushMagnitude,
                y: Math.sin(angle) * pushMagnitude
              };

              setTimeout(() => {
                get().applyGrenadeForce(player.id, forceVector, 1500);
              }, 0);
            }
          });
        }

        existingById.set(normalizedNewGrenade.id, normalizedNewGrenade);
        processedGrenadeThrowIds.add(normalizedNewGrenade.id);
        cleanupProcessedGrenadeIds();
        hasChanges = true;
      });

      if (!hasChanges) {
        return state;
      }

      return {
        activeGrenadeProjectiles: Array.from(existingById.values())
      };
    });
  },

  /**
   * Set the grenade charge start time (when Shift key is pressed)
   */
  setGrenadeChargeStartTime: (time: number | null) => {
    set({ grenadeChargeStartTime: time });
  },

  });
});
